import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useAuth0 } from "@auth0/auth0-react"
import {
  Container, Row, Col, Card, CardBody, CardTitle, Table, Badge, Button,
  Input, Label, FormGroup, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter,
  Nav, NavItem, NavLink, TabContent, TabPane, Collapse,
  ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap"
import classnames from "classnames"
import Select from "react-select"
import { withTranslation } from "react-i18next"

import { Permissions, EVIDENCE_REQUIRED_COMPANY_IDS } from "../../constants/enums"
import {
  getServiceValidationDetail,
  postServiceValidationValidate,
  postServiceValidationUnvalidate,
  postServiceValidationCancel,
  postServiceValidationSupport,
  postServiceValidationRebuildRoute,
  postServiceValidationRegeocode,
  postServiceValidationHold,
  postServiceValidationRelease,
  patchServiceValidationEdit,
  getServiceValidationAudit,
  getServiceAdmin,
  getTripsTimeline,
} from "../../helpers/api_rombo_helper"
import { listPlaces } from "../../common/data"
import { setDefaults as setGeocodeDefaults, fromAddress } from "react-geocode"
import ServiceRouteMap from "../../components/Common/ServiceRouteMap"
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd"
import PlaceAutocompleteInput from "../../components/Common/PlaceAutocompleteInput"
import MapStatusMessage from "../../components/Common/MapStatusMessage"
import { decodePolyline5 } from "../../helpers/gpx"
import { fmtMxDateTime } from "../../helpers/datetime"
import { fmtMXN } from "../../helpers/currency"
import CopyCoordsButton from "./CopyCoordsButton"
import { getNavContext, getNextPendingCode, getPrevCode, getPositionInContext } from "./navContext"
import { fieldLabel, formatFieldValue, formatAmountBreakdown } from "./auditFormat"
import ServiceValidationPods from "./ServiceValidationPods"
import ServiceValidationTour from "./ServiceValidationTour"
import ServiceValidationTimeline from "./ServiceValidationTimeline"

const CLOSE_OPTIONS = [
  { value: "na", label: "N/A" },
  { value: "in_zone", label: "En zona" },
  { value: "out_zone", label: "Fuera de zona" },
  { value: "foreign", label: "Foráneo" },
]

// Opciones de tipo de cierre para el overlay "Cierre sin cliente": excluye "na"
// (el cierre sin cliente exige un tipo concreto: en zona / fuera de zona / foráneo).
const CLOSE_OPTIONS_NO_NA = CLOSE_OPTIONS.filter((o) => o.value !== "na")

// ---- Helpers de presentación ------------------------------------------------

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v)

// Trunca texto a `max` caracteres con elipsis, mostrando el texto completo en
// un tooltip (title). Vacío -> "—". Usado en celdas de escala (origen/destino/
// pasajero/centro de costos) para no desbordar la tabla.
const ELLIPSIS_MAX = 12
const ellipsis = (v, max = ELLIPSIS_MAX) => {
  if (v === null || v === undefined || v === "") return "—"
  const s = String(v)
  if (s.length <= max) return s
  return <span title={s}>{s.slice(0, max)}…</span>
}

// Plaza: id numérico mapeado a etiqueta vía listPlaces() (mismo patrón que la
// Lista). Devuelve la label o null si no hay id.
const PLACES = listPlaces()
const placeLabel = (id) => {
  if (id == null || id === "") return null
  const p = PLACES.find((x) => String(x.value) === String(id))
  return p?.label || id
}

// Geocoding Google para escalas editadas/nuevas (dirección -> coords). Se
// configura una vez con la key del proyecto.
// Config de react-geocode (v1.0.0-alpha): outputFormat es requerido; sin él la
// petición no se arma bien y el geocoding falla en silencio.
const GEOCODE_KEY = import.meta.env.VITE_APP_SECRET_GOOGLE
setGeocodeDefaults({
  key: GEOCODE_KEY,
  language: "es",
  region: "mx",
  outputFormat: "json",
})

// Geocodifica una dirección a {lat,lng} o null (tolerante a fallo/sin key). La
// key se pasa explícita a fromAddress por robustez (no solo vía setDefaults).
const geocodeAddress = async (address) => {
  if (!address || !String(address).trim()) return null
  try {
    const resp = await fromAddress(String(address), GEOCODE_KEY, "es", "mx")
    const loc = resp?.results?.[0]?.geometry?.location
    if (loc && Number.isFinite(Number(loc.lat)) && Number.isFinite(Number(loc.lng))) {
      return { lat: Number(loc.lat), lng: Number(loc.lng) }
    }
  } catch (e) {
    // Sin conectividad/quota/clave: se omite el geocoding (coords quedan null).
    console.warn("[geocode] fallo al geocodificar la dirección:", e?.message || e)
  }
  return null
}

// Etiqueta de acción de bitácora legible (validate/unvalidate/edit/baseline...).
const ACTION_LABELS = {
  validate: "Validación",
  unvalidate: "Invalidación",
  edit: "Edición",
  baseline: "Baseline",
  create: "Creación",
  hold: "Tomado (on-hold)",
  release: "Liberado (on-hold)",
  pod_add: "Evidencia agregada",
  pod_update: "Evidencia actualizada",
  pod_delete: "Evidencia eliminada",
  route_rebuild: "Reconstrucción de ruta",
}
const actionLabel = (a) => ACTION_LABELS[a] || a || "—"

// Fila etiqueta/valor de la sección principal (estilo ERP, tabular).
const KV = ({ label, children }) => (
  <div className="d-flex justify-content-between py-1 border-bottom">
    <span className="text-muted">{label}</span>
    <span className="fw-medium text-end">{children}</span>
  </div>
)

// Check booleano compacto (Validado / En revisión / etc.).
const Flag = ({ label, on }) => (
  <div className="d-flex align-items-center gap-2 py-1">
    <i className={`bx ${on ? "bx-check-circle text-success" : "bx-circle text-muted"}`} />
    <span className={on ? "" : "text-muted"}>{label}</span>
  </div>
)

const ServiceValidationDetail = ({ t }) => {
  const { code } = useParams()
  const navigate = useNavigate()
  const { getAccessTokenSilently, user } = useAuth0()
  // Sub (identificador) del usuario autenticado, para saber si el on-hold del
  // servicio es propio (comparando con detail.onhold_by). Fuente fiable que
  // persiste tras recargar (no depende de un flag de sesión).
  const currentSub = user?.sub || null
  // Contexto de navegación (vista/filtros/orden) desde la lista o kanban.
  const navCtx = useMemo(() => getNavContext(), [])
  const returnUrl = navCtx?.returnUrl || "/service-validation"
  const returnView = navCtx?.view === "kanban" ? "Kanban" : t("List")
  const nextCode = useMemo(() => getNextPendingCode(code), [code])
  // Servicio anterior en la secuencia de navegación (para el botón "Atrás").
  const prevCode = useMemo(() => getPrevCode(code), [code])
  // Contador de posición "N/total" (F5): SOLO en modo lista plana (el navContext
  // trae mode/page/size/total). En agrupado o recarga directa devuelve null y el
  // contador no se renderiza (degradación con gracia).
  const listPosition = useMemo(() => getPositionInContext(code), [code])
  const [infoUser, setInfoUser] = useState({ permissions: [] })

  const [detail, setDetail] = useState(null)
  const [tripService, setTripService] = useState(null)
  const [timeline, setTimeline] = useState([])
  const [audit, setAudit] = useState({ versions: [], changes: [] })
  const [isLoading, setLoading] = useState(true)
  const [close, setClose] = useState("")
  const [notes, setNotes] = useState("")
  // Overlay "Cierre sin cliente" (PT-4066): abre un modal con el tipo de cierre
  // (in_zone/out_zone/foreign) y, al aceptar, valida con close_without_customer.
  const [closeNoCustomerOpen, setCloseNoCustomerOpen] = useState(false)
  const [cwcClose, setCwcClose] = useState("")
  // Overlay de confirmación previo a Validar (PT-4066).
  const [validateConfirmOpen, setValidateConfirmOpen] = useState(false)
  // Tab de Montos (PT-4066): recálculo vía backend + edición solo hacia abajo.
  // `amountEdits` null = sin edición (muestra valores del backend); al editar se
  // inicializa con los montos actuales. `amountErrors` marca intentos al alza.
  const [recalcAmounts, setRecalcAmounts] = useState(false)
  const [amountEdits, setAmountEdits] = useState(null)
  const [amountErrors, setAmountErrors] = useState({})
  // Edición del monto proveedor opcional (PT-4070): el input editable solo
  // aparece tras pulsar "Calcular montos" (amountsCalculated) Y activar el
  // toggle "Editar" (amountEditOpen). Por defecto el monto se muestra en solo
  // lectura. No se borra amountEdits al cerrar el toggle.
  const [amountsCalculated, setAmountsCalculated] = useState(false)
  const [amountEditOpen, setAmountEditOpen] = useState(false)
  // Check de confirmación del cambio manual del monto proveedor (PT-4075): el
  // monto editado NO se aplica/envía hasta que el usuario marque explícitamente
  // este checkbox. Se resetea al abrir el toggle de edición, al cambiar el valor
  // del input y tras recalcular montos, para que una confirmación previa no
  // "arrastre" un cambio nuevo sin revisar.
  const [amountEditConfirmed, setAmountEditConfirmed] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [acting, setActing] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const [supportSubject, setSupportSubject] = useState("")
  const [supportDescription, setSupportDescription] = useState("")
  // Modal de confirmación de cancelación de servicio (motivo opcional).
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState("")
  const [editMode, setEditMode] = useState(false)
  const [editScales, setEditScales] = useState([])
  const [saving, setSaving] = useState(false)
  // Sub-sección activa de las tarjetas de servicio (Escalas/Evidencias/
  // Pasajeros/Timeline). El stepper de 2 pasos se eliminó: el flujo de montos
  // (Calcular/Editar/Validar) vive ahora en el panel lateral "Montos del
  // servicio".
  const [panel, setPanel] = useState("scales")
  // Errores de edición por fila de escala (clave = índice, valor = mensaje).
  // Se usa para bloquear subir kms/duración por encima del valor original.
  const [editErrors, setEditErrors] = useState({})
  const [auditOpen, setAuditOpen] = useState(false)
  // Tutorial interactivo de la vista de validación (disparo manual).
  const [tourOpen, setTourOpen] = useState(false)
  // Menú de opciones (Comparación de versiones · Ver tutorial) en un dropdown.
  const [optionsOpen, setOptionsOpen] = useState(false)
  // Menú de herramientas de escalas (Actualizar direcciones · Reconstrucción
  // de ruta). `scaleToolsNew` muestra un indicador "Nuevo" que se oculta tras
  // el primer uso (persistido en localStorage para no volver a mostrarlo).
  const [scaleToolsOpen, setScaleToolsOpen] = useState(false)
  const [scaleToolsNew, setScaleToolsNew] = useState(
    () => {
      try { return localStorage.getItem("sv_scale_tools_seen") !== "1" }
      catch (_e) { return true }
    },
  )
  const [regeocoding, setRegeocoding] = useState(false)
  const dismissScaleToolsNew = () => {
    setScaleToolsNew(false)
    try { localStorage.setItem("sv_scale_tools_seen", "1") } catch (_e) { /* noop */ }
  }
  // Mapa expandible: al enfocar un punto (origen/destino/escala) el mapa crece
  // para una visualización cómoda. `mapExpanded` controla la altura; `focusPoint`
  // resalta el punto elegido bajo el mapa. Ref para hacer scroll suave al mapa.
  const [mapExpanded, setMapExpanded] = useState(false)
  const [focusPoint, setFocusPoint] = useState(null)
  // Overlay de desviación de dirección (clic en "ver desviación" de una escala):
  // par de geopuntos creación/operador + círculo con la desviación en metros.
  const [deviationOverlay, setDeviationOverlay] = useState(null)
  // A2: toggle para mostrar la dirección ORIGINAL (textual, o coords para el
  // operador) bajo cada geopunto del overlay de desviación.
  const [showDeviationAddresses, setShowDeviationAddresses] = useState(false)
  // A3: filas expandidas (por índice) de la edición de escalas que muestran la
  // dirección original de creación en una fila colapsable.
  const [expandedRows, setExpandedRows] = useState(() => new Set())
  const [rebuildingRoute, setRebuildingRoute] = useState(false)
  const [mapFullscreen, setMapFullscreen] = useState(false)
  const mapCardRef = useRef(null)

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ data: Login.data, loading: Login.loading }),
  )
  const { data, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (data?.data) setInfoUser(data.data)
  }, [data, loading])

  const authHeader = useCallback(async () => {
    const accessToken = await getAccessTokenSilently()
    return { Authorization: `Bearer ${accessToken}` }
  }, [getAccessTokenSilently])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAmountsCalculated(false)
    setAmountEditOpen(false)
    try {
      const token = await authHeader()
      const d = await getServiceValidationDetail(token, code)
      setDetail(d)
      setClose(d?.close || "")
      setNotes(d?.notes || "")
      let svc = null
      try {
        svc = await getServiceAdmin(token, code, {})
        setTripService(svc || null)
      } catch (_e) {
        setTripService(null)
      }
      // Timeline (recorrido de eventos) — tolerante a fallo.
      try {
        const tl = await getTripsTimeline(token, { booking_code: svc?.code || code })
        const flat = (tl?.trip || []).map((obj) => Object.values(obj)[0])
        setTimeline(flat)
      } catch (_e) {
        setTimeline([])
      }
      try {
        const a = await getServiceValidationAudit(token, code)
        setAudit(a || { versions: [], changes: [] })
      } catch (_e) {
        setAudit({ versions: [], changes: [] })
      }
    } catch (e) {
      setError(t("LoadError"))
    } finally {
      setLoading(false)
    }
  }, [authHeader, code, t])

  useEffect(() => { load() }, [load])

  // Las notificaciones superiores (éxito/error) se auto-cierran a los 10s. El
  // usuario también puede cerrarlas manualmente (toggle del Alert). El timer se
  // reinicia cada vez que cambia el mensaje y se limpia al desmontar.
  useEffect(() => {
    if (!success && !error) return undefined
    const timer = setTimeout(() => { setSuccess(null); setError(null) }, 10000)
    return () => clearTimeout(timer)
  }, [success, error])

  const canRead = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_READ)
  const canValidate = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATE)
  const canWrite = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_WRITE)
  const validated = !!detail?.validated

  // On-hold de validación. El dueño del hold se deriva comparando el
  // `onhold_by` del detalle con el `sub` del usuario autenticado, de modo que el
  // estado es fiel incluso tras recargar la página (no depende de un flag de
  // sesión). El backend sigue siendo la fuente de verdad del bloqueo (409).
  const onholdBy = detail?.onhold_by || null
  // Resuelve el nombre legible de un usuario a partir del sub + el nombre
  // persistido (``*_by_name``). Si no hay nombre persistido (entradas antiguas)
  // y el sub es el del usuario ACTUAL, usa su full_name (de /api/v1/admin/me,
  // ya cargado en infoUser). Para otros usuarios sin nombre persistido, cae al
  // sub (``me`` solo resuelve al usuario propio).
  const resolveUserName = (sub, persistedName) => {
    if (persistedName) return persistedName
    if (sub && currentSub && sub === currentSub && infoUser?.full_name) {
      return infoUser.full_name
    }
    return sub || "—"
  }
  const onholdByName = resolveUserName(onholdBy, detail?.onhold_by_name)
  const heldByMe = !!onholdBy && !!currentSub && onholdBy === currentSub
  const heldByOther = !!onholdBy && !heldByMe
  // La edición solo se habilita si el usuario tomó el servicio (on-hold propio)
  // y no está validado. Sin hold propio no se puede editar (regla de negocio:
  // editar/validar solo el usuario que lo tomó).
  const canEditScales = canWrite && !validated && heldByMe

  // Fuente primaria: el detalle de validación ya trae trip + pods unificados
  // (backend get_detail ampliado). getServiceAdmin queda como complemento
  // opcional (algunos campos legacy) y el timeline viene aparte.
  const dt = detail?.trip || {}
  const tripStatus = dt.status || tripService?.status
  // Servicio cancelado: el Trip quedó en DELETED. No se puede tomar, validar ni
  // volver a cancelar; la UI inhabilita esos botones.
  const isCancelled = tripStatus === "DELETED"
  // Fuente ÚNICA de las evidencias: `detail.pods` (service-validation detail).
  // Es la única que transporta el `uuid` del POD (contrato PodDetail), necesario
  // para los flujos de escritura (aprobar/rechazar/eliminar/cambiar tipo/
  // reemplazar imagen) y para el visor. NO se cae a `tripService.pods`: esa
  // fuente (bookings_format) proyecta solo {url, type, status} y descarta el id,
  // lo que generaba URLs `.../pods/undefined` al actuar sobre esos PODs.
  const pods = detail?.pods || []
  // El recorrido del servicio en validación proviene de los trackings GRS
  // (mapLayers: original/sanitizada/reconstruida). Ya NO se resuelve el GPX vía
  // el endpoint trips/{code}/resume?type=gpx (se eliminó esa llamada); si no hay
  // capas GRS, se cae a la imagen del trip o al estado vacío.

  // Regla in_validation (misma que el backend): solo se puede validar si el
  // Trip está FINISHED y todos los PODs están APPROVED. Sirve para deshabilitar
  // el CTA y explicar por qué (menos idas y vueltas).
  // Solo bloquean las evidencias SIN revisar (PENDING). APPROVED y REJECTED
  // cuentan como revisadas y permiten validar (misma regla que el backend).
  const pendingPods = pods.filter((p) => {
    const s = (p.status || "").toUpperCase()
    return s !== "APPROVED" && s !== "REJECTED"
  }).length
  // Clientes (Syscom*, Ecla, Walmart) que exigen que EXISTAN evidencias/PODs
  // para poder validar. El company_id REAL ahora viene en el payload de detalle
  // como string; se antepone en la derivación y se castea a número para comparar
  // con la constante EVIDENCE_REQUIRED_COMPANY_IDS (array numérico). REGLA: basta
  // con que EXISTAN PODs (pods.length > 0), NO que estén APPROVED (no confundir
  // con pendingPods). Fail-open SOLO si company_id viene ausente/nulo/vacío: en
  // ese caso la regla NO bloquea; si está presente, aplica de verdad.
  const companyId = detail?.company_id ?? detail?.trip?.company_id ?? tripService?.company_id ?? tripService?.partner_id
  // Empresa EXENTA de la aprobación de PODs (empresa en la lista de exclusión
  // del backend): su servicio se valida sin PODs aprobados y el tab de
  // Evidencias se muestra DESHABILITADO (no oculto). El backend es la autoridad.
  const podsApprovalExempt = detail?.pods_approval_exempt === true
  const requiresEvidence = companyId != null && companyId !== "" && EVIDENCE_REQUIRED_COMPANY_IDS.includes(Number(companyId))
  const evidenceMissing = requiresEvidence && (pods?.length ?? 0) === 0
  const validateBlockedReason = useMemo(() => {
    if (validated) return null
    if (heldByOther) return t("HeldByOther") || "Tomado por otro usuario"
    if (tripStatus && tripStatus !== "FINISHED") return t("ValidateBlockedNotFinished")
    // Empresas exentas: los PODs pendientes NO bloquean la validación (el
    // backend ya no bloquea en ese caso).
    if (!podsApprovalExempt && pendingPods > 0) return t("ValidateBlockedPendingPods")
    if (evidenceMissing) return t("EvidenceRequiredForClient")
    // On-hold obligatorio: hay que tomar el servicio antes de validarlo.
    if (!heldByMe) return t("ValidateBlockedNotHeld") || "Toma el servicio (on-hold) antes de validar."
    return null
  }, [validated, heldByOther, heldByMe, tripStatus, pendingPods, evidenceMissing, podsApprovalExempt, t])
  // Si el servicio resulta exento y el panel activo era Evidencias, caer a
  // Escalas: el tab de Evidencias queda deshabilitado y no debe quedarse abierto.
  useEffect(() => {
    if (podsApprovalExempt && panel === "pods") setPanel("scales")
  }, [podsApprovalExempt, panel])
  const canDoValidate = canValidate && !validated && !validateBlockedReason
  // ¿Hay algún error de edición hacia arriba? Bloquea Guardar y Siguiente.
  const hasEditErrors = Object.keys(editErrors).length > 0
  // ¿Hay montos editados por encima del valor del backend? Bloquea validar/guardar.
  const hasAmountErrors = Object.keys(amountErrors).length > 0
  // ¿El monto proveedor editado difiere del valor del backend? (PT-4075) Solo
  // entonces hay un cambio manual real que requiere confirmación explícita.
  const amountDiffers =
    amountEdits?.supplier_amount != null
    && amountEdits.supplier_amount !== ""
    && Number(amountEdits.supplier_amount) !== Number(detail?.supplier_amount)
  // Edición manual del monto pendiente de confirmar: hay un cambio real y el
  // check de confirmación aún no está marcado. Bloquea validar y evita enviar el
  // monto editado hasta que el usuario confirme.
  const amountEditPending = amountEditOpen && amountDiffers && !amountEditConfirmed

  // ---- Datos derivados (prefiere detail.trip; fallback a getServiceAdmin) ----
  const legacyTrip = tripService?.trip || {}
  const legacyVehicle = tripService?.vehicle || {}
  const companyName = dt.partner_name || tripService?.partner_name
  // Solicitado por: nombre del solicitante del request (blob JSON `info` del
  // modelo requests). Mismo origen del que ya se leen origin/destination/riders
  // (tripService.info). Acceso opcional con fallbacks tolerantes; si no llega
  // por ninguna vía, queda null y la UI muestra "—" (ver nota de verificación:
  // el detalle no garantiza requested_by.name hoy, requeriría cambio backend).
  const requestedByName =
    tripService?.info?.requested_by?.name
    ?? dt?.info?.requested_by?.name
    ?? detail?.info?.requested_by?.name
    ?? null
  const driverName = dt.driver_name || tripService?.driver?.driver_name
  const plate = dt.plate || legacyVehicle.plate || legacyTrip.plate
  // Plaza del servicio: el backend ahora expone el NOMBRE real (string) en
  // detail.plaza. Se prefiere ese nombre; si viniera vacío, fallback al id
  // numérico (detail.trip.place / detail.place) mapeado a etiqueta con
  // listPlaces(), y finalmente al tripService legacy.
  const placeId = detail?.trip?.place ?? detail?.place ?? tripService?.place
  const plazaName = detail?.plaza ?? detail?.trip?.plaza ?? null
  const placeText = (plazaName && String(plazaName).trim()) ? plazaName : placeLabel(placeId)
  // Origen/destino del SERVICIO: fuente preferente pt_service_billing
  // (detail.service_origin/service_destination = extremos de las escalas:
  // origen de la 1a / destino de la última). Fallback al Trip solo si el
  // servicio aún no expone esos extremos (sin escalas con coords).
  const svcOrigin = detail?.service_origin || null
  const svcDestination = detail?.service_destination || null
  const originText = svcOrigin?.description
    || dt.origin
    || tripService?.info?.origin?.address?.description
  const destinationText = svcDestination?.description
    || dt.destination
    || tripService?.info?.destination?.address?.description
  const originLat = svcOrigin?.latitude
    ?? dt.origin_lat ?? tripService?.info?.origin?.address?.latlng?.latitude
  const originLng = svcOrigin?.longitude
    ?? dt.origin_lng ?? tripService?.info?.origin?.address?.latlng?.longitude
  const destinationLat = svcDestination?.latitude
    ?? dt.destination_lat ?? tripService?.info?.destination?.address?.latlng?.latitude
  const destinationLng = svcDestination?.longitude
    ?? dt.destination_lng ?? tripService?.info?.destination?.address?.latlng?.longitude
  const riders = (dt.riders && dt.riders.length ? dt.riders : tripService?.info?.riders) || []
  const startedAt = dt.started_at || legacyTrip.started_at
  const endedAt = dt.ended_at || legacyTrip.ended_at
  // Fecha/hora de solicitud: scheduled_start (getServiceAdmin) o el formateado
  // del trip. scheduled_start viene como {date,time,timezone}.
  const ss = tripService?.scheduled_start
  const requestedAt = ss?.date
    ? `${ss.date}T${ss.time || "00:00:00"}`
    : (legacyTrip.scheduled_start_at || null)
  const urlImage = legacyTrip.url_image
  const isRoutePlanned = dt.is_route_planned ?? tripService?.is_route_planned
  const vehicleDesc = [
    dt.vehicle_brand || legacyVehicle.brand,
    dt.vehicle_sub_brand || legacyVehicle.sub_brand,
    dt.vehicle_model || legacyVehicle.model,
    dt.vehicle_color || legacyVehicle.color,
  ].filter(Boolean).join(" ")

  // ---- Edición de escalas ---------------------------------------------------
  const startEdit = () => {
    const rows = (detail?.scales || []).map((sc) => ({
      // uuid: identidad estable de la escala (PK local). Es la clave con la que
      // el backend actualiza la escala existente; sin ella una escala sin
      // external_id se duplicaba al guardar.
      uuid: sc.uuid ?? null,
      external_id: sc.external_id ?? null,
      order: sc.order ?? null,
      // Orden original (para detectar reordenamiento manual).
      _origOrder: sc.order ?? null,
      origin: sc.origin ?? "",
      destination: sc.destination ?? "",
      // Textos originales para detectar cambios y re-geocodificar solo lo editado.
      _origOrigin: sc.origin ?? "",
      _origDestination: sc.destination ?? "",
      // Coords conocidas (del backend) por si no se re-edita el texto.
      origin_lat: sc.origin_lat ?? null,
      origin_lng: sc.origin_lng ?? null,
      destination_lat: sc.destination_lat ?? null,
      destination_lng: sc.destination_lng ?? null,
      geocoding: null, // "origin" | "destination" mientras resuelve
      // Datos adicionales de la escala. start/end_time al formato del input
      // datetime-local (YYYY-MM-DDTHH:MM): se recorta el ISO del backend.
      passenger: sc.passenger ?? "",
      cost_center: sc.cost_center ?? "",
      start_time: sc.start_time ? String(sc.start_time).slice(0, 16) : "",
      end_time: sc.end_time ? String(sc.end_time).slice(0, 16) : "",
      // Kms enteros (sin decimales) en la UI; se redondea al entero más cercano.
      kms: sc.kms != null ? Math.round(Number(sc.kms)) : 0,
      // La UI edita la duración en MINUTOS enteros (sin decimales); el backend la
      // almacena en segundos. Se redondea al minuto más cercano al editar.
      duration_min: sc.duration != null ? Math.round(Number(sc.duration) / 60) : 0,
      // Valores originales (para detectar cambios y habilitar reconstrucción).
      _origKms: sc.kms != null ? Math.round(Number(sc.kms)) : 0,
      _origDurationMin: sc.duration != null ? Math.round(Number(sc.duration) / 60) : 0,
      deleted: false,
    }))
    setEditScales(rows)
    setEditErrors({})
    setEditMode(true)
  }

  const cancelEdit = () => { setEditMode(false); setEditScales([]); setEditErrors({}) }
  const changeScale = (idx, field, value) =>
    setEditScales((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)))

  // Edición numérica SOLO HACIA ABAJO: kms y duración (en minuto redondeado) no
  // pueden superar el valor original de la fila. Si el usuario intenta subir, el
  // valor se acepta tal cual. `round` redondea la duración a minuto entero
  // (Math.round). Hotfix (PT-4078): se permite editar kms/duración POR ESCALA
  // hacia ARRIBA; ya no se topa al original ni se marca error por "subir" (el
  // backend aplica las reglas de totales down-only del servicio). Se conserva
  // el nombre/firma para no tocar los call-sites; el estado de error de fila se
  // mantiene por compatibilidad aunque ya no se pueble por esta causa.
  const changeScaleDown = (idx, field, origField, value, { round = false } = {}) =>
    setEditScales((prev) => prev.map((s, i) => {
      if (i !== idx) return s
      if (value === "") {
        setEditErrors((e) => { const n = { ...e }; delete n[idx]; return n })
        return { ...s, [field]: "" }
      }
      let num = Number(value)
      if (Number.isNaN(num)) return s
      if (round) num = Math.round(num)
      setEditErrors((e) => { const n = { ...e }; delete n[idx]; return n })
      return { ...s, [field]: num }
    }))

  // ¿Hay cambios en las escalas en edición? (escala nueva, marcada para borrar,
  // o con origen/destino/kms/duración distintos a los originales). Habilita la
  // reconstrucción de ruta: solo tiene sentido reconstruir si cambió el trazado.
  const scalesDirty = useMemo(() => {
    if (!editMode) return false
    return editScales.some((s) => {
      if (!s.uuid) return true                  // escala nueva (sin PK local)
      if (s.deleted) return true                // marcada para borrar
      if (s.origin !== s._origOrigin) return true
      if (s.destination !== s._origDestination) return true
      if (s._origKms != null && Number(s.kms) !== Number(s._origKms)) return true
      if (s._origDurationMin != null
        && Number(s.duration_min) !== Number(s._origDurationMin)) return true
      // Reordenamiento manual: el order cambió respecto al original.
      if (s._origOrder != null && Number(s.order) !== Number(s._origOrder)) return true
      return false
    })
  }, [editMode, editScales])

  // Drag&drop: al soltar, mueve la fila de source.index a destination.index y
  // reasigna order secuencial sobre las activas. Index es la posición de render
  // en editScales (incluye filas borradas, que no se reordenan en el payload).
  const onScalesDragEnd = (result) => {
    const { source, destination } = result || {}
    if (!destination || destination.index === source.index) return
    setEditScales((prev) => {
      const next = [...prev]
      const [moved] = next.splice(source.index, 1)
      next.splice(destination.index, 0, moved)
      let seq = 1
      return next.map((s) => (s.deleted ? s : { ...s, order: seq++ }))
    })
  }

  // Selección de una opción del autocomplete de Google: fija el texto y las
  // coords (lat/lng) de la escala en el campo indicado ("origin"|"destination").
  const selectScalePlace = (idx, field, place) => {
    setEditScales((prev) => prev.map((s, i) => {
      if (i !== idx) return s
      const upd = { ...s, [field]: place.description }
      if (field === "origin") {
        upd.origin_lat = place.lat ?? null
        upd.origin_lng = place.lng ?? null
      } else {
        upd.destination_lat = place.lat ?? null
        upd.destination_lng = place.lng ?? null
      }
      return upd
    }))
  }
  const addScale = () =>
    setEditScales((prev) => {
      // Si existe una escala previa (activa, no borrada), el ORIGEN del nuevo
      // segmento se encadena con el DESTINO de esa escala: mismo texto y coords,
      // y queda bloqueado (no editable) para mantener la cadena origen->destino.
      const lastActive = [...prev].reverse().find((s) => !s.deleted)
      const chained = !!lastActive
      return [
        ...prev,
        {
          uuid: null, external_id: null, order: prev.length + 1,
          origin: chained ? (lastActive.destination ?? "") : "",
          destination: "",
          _origOrigin: "", _origDestination: "",
          origin_lat: chained ? (lastActive.destination_lat ?? null) : null,
          origin_lng: chained ? (lastActive.destination_lng ?? null) : null,
          destination_lat: null, destination_lng: null,
          // Bloquea la edición del origen cuando viene encadenado de la escala
          // anterior (georeversa ya resuelta en el destino previo).
          originLocked: chained,
          passenger: "", cost_center: "", start_time: "", end_time: "",
          geocoding: null, kms: 0, duration_min: 0, deleted: false,
        },
      ]
    })
  const removeScale = (idx) =>
    setEditScales((prev) => {
      const row = prev[idx]
      // Escala nueva (sin uuid): se quita de la lista. Persistida: se marca
      // deleted para que el backend la dé de baja al guardar.
      if (!row.uuid) return prev.filter((_, i) => i !== idx)
      return prev.map((s, i) => (i === idx ? { ...s, deleted: !s.deleted } : s))
    })

  // Guarda la edición de escalas. Con `thenRebuild=true` ("Guardar y planear
  // ruta"), tras persistir dispara la reconstrucción GRS: el backend parte de
  // las escalas ya guardadas, así que el orden guardar->reconstruir es correcto.
  const saveEdit = async ({ thenRebuild = false } = {}) => {
    // El tipo de cierre ya NO es obligatorio en el flujo normal (PT-4066): se
    // envía `close` tal cual (cadena vacía si no se eligió). El backend sigue
    // recalculando montos a tres niveles con el valor recibido.
    setError(null); setSuccess(null); setSaving(true)
    try {
      const token = await authHeader()
      // Las coords se resuelven al salir del input (onBlur, geocodeScaleField).
      // Aquí, como respaldo, se geocodifica lo que cambió y aún no tenga coords
      // (p. ej. si el usuario guardó sin desenfocar el campo).
      const scales = await Promise.all(editScales.map(async (s) => {
        const isNew = !s.uuid
        const originChanged = isNew || s.origin !== s._origOrigin
        const destChanged = isNew || s.destination !== s._origDestination
        let oLat = s.origin_lat, oLng = s.origin_lng
        let dLat = s.destination_lat, dLng = s.destination_lng
        if (!s.deleted && originChanged && oLat == null) {
          const c = await geocodeAddress(s.origin)
          if (c) { oLat = c.lat; oLng = c.lng }
        }
        if (!s.deleted && destChanged && dLat == null) {
          const c = await geocodeAddress(s.destination)
          if (c) { dLat = c.lat; dLng = c.lng }
        }
        return {
          uuid: s.uuid,
          external_id: s.external_id,
          order: s.order,
          origin: s.origin || null,
          destination: s.destination || null,
          kms: s.kms === "" ? null : Number(s.kms),
          // De minutos (UI) a segundos (almacenamiento).
          duration: s.duration_min === "" || s.duration_min == null
            ? null
            : Math.round(Number(s.duration_min) * 60),
          origin_lat: oLat ?? null,
          origin_lng: oLng ?? null,
          destination_lat: dLat ?? null,
          destination_lng: dLng ?? null,
          // Datos adicionales de la escala.
          passenger: s.passenger || null,
          cost_center: s.cost_center || null,
          start_time: s.start_time || null,
          end_time: s.end_time || null,
          deleted: !!s.deleted,
        }
      }))
      await patchServiceValidationEdit(token, code, { scales, notes, close })
      setEditMode(false); setEditScales([]); setEditErrors({})
      if (thenRebuild) {
        // Reconstruye con las escalas recién persistidas.
        setRebuildingRoute(true)
        try {
          await postServiceValidationRebuildRoute(token, code)
          setSuccess(t("ChangesSavedAndRouteRebuilt") || "Cambios guardados y ruta planeada")
        } catch (_e) {
          setSuccess(t("ChangesSavedOk"))
          setError(t("RouteRebuildError") || "No se pudo reconstruir el recorrido")
        } finally {
          setRebuildingRoute(false)
        }
      } else {
        setSuccess(t("ChangesSavedOk"))
      }
      await load()
    } catch (e) {
      const status = e?.response?.status
      if (status === 502) setError(t("BillingUnavailable"))
      else if (status === 409) setError(t("AlreadyValidated"))
      else setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setSaving(false)
    }
  }

  const handleValidate = async () => {
    // 'close' (tipo de cierre) es obligatorio y viaja al backend. La UI NO
    // recalcula montos: el backend es la fuente de verdad y tarifica a tres
    // niveles (cliente/plaza/tipo de unidad) en
    // erp/addons_rombo/rombomx_service/models/rombomx_service_pt.py:81.
    // La billing/api legacy ya no es válida para estos cálculos.
    setError(null); setSuccess(null); setActing(true)
    try {
      const token = await authHeader()
      // Monto proveedor editado a la baja (PT-4066): se envía como
      // requested_supplier_amount; el backend aplica la regla no-subir. Solo si
      // hubo edición (amountEdits) y no hay errores pendientes.
      const args = { close, notes }
      const reqSupplier = amountEdits?.supplier_amount
      // Solo se envía el monto editado si no hay errores Y el cambio manual fue
      // confirmado con el check (PT-4075). Sin confirmación no se adjunta
      // requested_supplier_amount: el backend conserva el monto calculado.
      if (!hasAmountErrors && amountEditConfirmed && reqSupplier != null && reqSupplier !== "") {
        args.requested_supplier_amount = Number(reqSupplier)
      }
      await postServiceValidationValidate(token, code, args)
      setSuccess(t("ServiceValidatedOk"))
      await load()
    } catch (e) {
      const status = e?.response?.status
      if (status === 502) setError(t("BillingUnavailable"))
      else if (status === 409) setError(e?.response?.data?.detail || t("AlreadyValidated"))
      else setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setActing(false)
    }
  }

  // Cierre sin cliente (PT-4066): valida enviando close_without_customer=true y
  // el tipo de cierre elegido en el overlay. El backend solo exige `close`
  // cuando close_without_customer=true, por eso el flujo normal ya no lo pide.
  const handleCloseWithoutCustomer = async () => {
    if (!cwcClose) return
    setError(null); setSuccess(null); setActing(true)
    try {
      const token = await authHeader()
      await postServiceValidationValidate(token, code, {
        close: cwcClose, close_without_customer: true, notes,
      })
      setCloseNoCustomerOpen(false)
      setSuccess(t("ServiceValidatedOk"))
      await load()
    } catch (e) {
      const status = e?.response?.status
      if (status === 502) setError(t("BillingUnavailable"))
      else if (status === 409) setError(e?.response?.data?.detail || t("AlreadyValidated"))
      else setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setActing(false)
    }
  }

  // Construye el payload de `scales` a partir del detalle actual (sin cambios de
  // escala ni geocoding): mismo shape que envía saveEdit. Se usa para recalcular
  // montos vía backend sin alterar las escalas.
  const buildScalesFromDetail = () =>
    (detail?.scales || []).map((sc) => ({
      uuid: sc.uuid ?? null,
      external_id: sc.external_id ?? null,
      order: sc.order ?? null,
      origin: sc.origin || null,
      destination: sc.destination || null,
      kms: sc.kms == null ? null : Number(sc.kms),
      duration: sc.duration == null ? null : Math.round(Number(sc.duration)),
      origin_lat: sc.origin_lat ?? null,
      origin_lng: sc.origin_lng ?? null,
      destination_lat: sc.destination_lat ?? null,
      destination_lng: sc.destination_lng ?? null,
      passenger: sc.passenger || null,
      cost_center: sc.cost_center || null,
      start_time: sc.start_time || null,
      end_time: sc.end_time || null,
      deleted: false,
    }))

  // Calcular montos (PT-4066): NO calcula en UI. Dispara el PATCH de edición con
  // las escalas actuales + notes + close, que hace que el backend recalcule los
  // montos a tres niveles, y luego recarga el detalle para traer los montos
  // recalculados. Tras recalcular, descarta ediciones manuales previas.
  const handleCalculateAmounts = async () => {
    setError(null); setSuccess(null); setRecalcAmounts(true)
    try {
      const token = await authHeader()
      await patchServiceValidationEdit(token, code, {
        scales: buildScalesFromDetail(), notes, close,
      })
      await load()
      setAmountEdits(null)
      setAmountErrors({})
      // Tras recalcular (y después del load que lo reseteó) se habilita la
      // opción de edición manual del monto proveedor vía el toggle "Editar".
      setAmountsCalculated(true)
      setAmountEditOpen(false)
      setAmountEditConfirmed(false)
      setSuccess(t("AmountsCalculatedOk"))
    } catch (e) {
      const status = e?.response?.status
      if (status === 502) setError(t("BillingUnavailable"))
      else if (status === 409) setError(e?.response?.data?.detail || t("AlreadyValidated"))
      else setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setRecalcAmounts(false)
    }
  }

  // Edición del monto proveedor. Hotfix (PT-4078): se permite editar el monto
  // proveedor hacia ARRIBA o hacia abajo; ya no se topa al original ni se marca
  // error por "subir" (el backend respeta el override en ambos sentidos). Se
  // conserva el nombre/firma (y el parámetro `orig`) para no tocar los
  // call-sites; el estado de error se mantiene por compatibilidad aunque ya no
  // se pueble por esta causa.
  const changeAmountDown = (field, orig, value) => {
    // Cualquier cambio en el valor invalida una confirmación previa (PT-4075):
    // el usuario debe volver a marcar el check tras modificar el monto.
    if (field === "supplier_amount") setAmountEditConfirmed(false)
    return setAmountEdits((prev) => {
      const base = prev || {}
      if (value === "") {
        setAmountErrors((e) => { const n = { ...e }; delete n[field]; return n })
        return { ...base, [field]: "" }
      }
      const num = Number(value)
      if (Number.isNaN(num)) return base
      setAmountErrors((e) => { const n = { ...e }; delete n[field]; return n })
      return { ...base, [field]: num }
    })
  }

  // Validación en 0s: valida el servicio forzando montos y kms/duración a 0
  // (sin llamar al cálculo de billing). Requiere las mismas condiciones que la
  // validación normal (servicio tomado por el usuario, no cancelado).
  const handleValidateZero = async () => {
    setOptionsOpen(false)
    setError(null); setSuccess(null); setActing(true)
    try {
      const token = await authHeader()
      await postServiceValidationValidate(token, code, { close, notes, zero_amounts: true })
      setSuccess(t("ServiceValidatedZeroOk") || "Servicio validado en 0s")
      await load()
    } catch (e) {
      const status = e?.response?.status
      if (status === 409) setError(e?.response?.data?.detail || t("AlreadyValidated"))
      else setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setActing(false)
    }
  }

  const handleHold = async () => {
    setError(null); setSuccess(null); setActing(true)
    try {
      const token = await authHeader()
      await postServiceValidationHold(token, code)
      setSuccess(t("ServiceHeldOk") || "Servicio tomado para validación")
      await load()
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setActing(false)
    }
  }

  const handleRelease = async () => {
    setError(null); setSuccess(null); setActing(true)
    try {
      const token = await authHeader()
      await postServiceValidationRelease(token, code)
      setSuccess(t("ServiceReleasedOk") || "On-hold liberado")
      await load()
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setActing(false)
    }
  }

  const handleUnvalidate = async () => {
    setError(null); setSuccess(null); setActing(true)
    try {
      const token = await authHeader()
      await postServiceValidationUnvalidate(token, code, { notes })
      setSuccess(t("ServiceUnvalidatedOk"))
      await load()
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setActing(false)
    }
  }

  // Cancela el servicio (Trip -> DELETED en pt-trip; evento CANCELLED al ERP).
  // El motivo (opcional) viaja como notes. Tras cancelar, recarga el detalle.
  const handleCancel = async () => {
    // El motivo es obligatorio (también validado en backend).
    const reason = (cancelReason || "").trim()
    if (!reason) {
      setError(t("CancelReasonRequired") || "El motivo de cancelación es obligatorio.")
      return
    }
    setError(null); setSuccess(null); setActing(true)
    try {
      const token = await authHeader()
      await postServiceValidationCancel(token, code, { notes: reason })
      setCancelOpen(false)
      setCancelReason("")
      setSuccess(t("ServiceCancelledOk") || "Servicio cancelado")
      await load()
    } catch (e) {
      setError(e?.response?.data?.detail || t("ValidationError"))
    } finally {
      setActing(false)
    }
  }

  const handleSupport = async () => {
    setError(null); setSuccess(null)
    try {
      const token = await authHeader()
      await postServiceValidationSupport(token, code, {
        subject: supportSubject,
        description: supportDescription,
      })
      setSupportOpen(false)
      setSupportSubject(""); setSupportDescription("")
      setSuccess(t("SupportTicketCreated"))
    } catch (e) {
      setError(t("SupportError"))
    }
  }

  // Enfoca el mapa GPX en un punto (origen/destino/escala): centra el viewer en
  // la coordenada (vía hash de URL, remontando el iframe), lo expande para una
  // visualización cómoda y hace scroll suave hasta él. Best-effort: si no hay
  // coordenadas, no hace nada (el clic queda inerte y el cursor no sugiere acción).
  const focusOnMap = useCallback((lat, lng, label) => {
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return
    setFocusPoint({ lat: Number(lat), lng: Number(lng), zoom: 16, label })
    setMapExpanded(true)
    // Scroll al mapa tras el re-render (el iframe remonta con la nueva key).
    requestAnimationFrame(() => {
      mapCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    })
  }, [])
  const hasPoint = (lat, lng) =>
    Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))

  // Enfoca el mapa en la desviación de dirección de una escala y extremo
  // ("origin"|"destination"): arma el par creación/operador y la etiqueta de
  // desviación, y expande el mapa (reusa mapExpanded/scroll como focusOnMap).
  // Solo tiene efecto si hay geopunto de creación y de operador para el extremo.
  const focusDeviation = useCallback((sc, end) => {
    const cLat = Number(sc?.[`created_${end}_lat`])
    const cLng = Number(sc?.[`created_${end}_lng`])
    const oLat = Number(sc?.[`operator_${end}_lat`])
    const oLng = Number(sc?.[`operator_${end}_lng`])
    if (!Number.isFinite(cLat) || !Number.isFinite(cLng)) return
    if (!Number.isFinite(oLat) || !Number.isFinite(oLng)) return
    const devRaw = sc?.[`${end}_deviation_m`]
    const deviationM = devRaw == null ? null : Number(devRaw)
    const toleranceM = Number(detail?.address_deviation_tolerance_m) || 100
    const label = deviationM != null
      ? t("DeviationMeters", { m: Math.round(deviationM) })
      : t("ToleranceNoDeviation", { m: Math.round(toleranceM) })
    // Dirección TEXTUAL original de creación por extremo (origin/destination).
    // El operador no tiene texto → null (el mapa cae a coords si el toggle está
    // activo). A2.
    const createdAddress = (end === "origin" ? sc?.origin : sc?.destination) || null
    setDeviationOverlay({
      created: { lat: cLat, lng: cLng, address: createdAddress },
      operator: { lat: oLat, lng: oLng, address: null },
      deviationM,
      toleranceM,
      label,
    })
    setFocusPoint(null)
    setMapExpanded(true)
    requestAnimationFrame(() => {
      mapCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    })
  }, [detail, t])

  // A3: alterna la fila colapsable (por índice) que muestra la dirección
  // original de creación de la escala en la edición de escalas.
  const toggleScaleOriginal = useCallback((i) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }, [])

  // Badge de desviación + botón "ver desviación" para un extremo de la escala.
  // Badge: color `danger` si excede la tolerancia, `success` si no, `secondary`
  // (—) si no hay desviación calculable. El botón se habilita solo con los
  // geopuntos de creación y operador presentes para ese extremo.
  const renderDeviation = (sc, end) => {
    const devRaw = sc?.[`${end}_deviation_m`]
    const dev = devRaw == null ? null : Number(devRaw)
    const exceeds = sc?.[`${end}_exceeds_tolerance`]
    const hasCreated = hasPoint(sc?.[`created_${end}_lat`], sc?.[`created_${end}_lng`])
    const hasOperator = hasPoint(sc?.[`operator_${end}_lat`], sc?.[`operator_${end}_lng`])
    const canShow = hasCreated && hasOperator
    const badgeColor = dev == null ? "secondary" : (exceeds === true ? "danger" : "success")
    const badgeText = dev == null ? "—" : `${Math.round(dev)} m`
    return (
      <div className="d-flex align-items-center gap-1 mt-1">
        <Badge color={badgeColor} pill
          title={t("AddressDeviationTitle") || "Desviación creación vs operador"}>
          {badgeText}
        </Badge>
        {canShow && (
          <button type="button" className="btn btn-link p-0 text-decoration-none"
            onClick={() => focusDeviation(sc, end)}
            title={t("ShowDeviationOnMap") || "Ver desviación en el mapa"}>
            <i className="bx bx-target-lock text-primary" />
          </button>
        )}
      </div>
    )
  }

  // Validación automática: bandera del contrato GRS (detail.auto_validated).
  const autoValidated = !!(detail?.auto_validated ?? dt.auto_validated)

  // Salud de la ruta: consumo directo del contrato GRS (route_health del
  // detalle), no heurística cliente. level ∈ critical|low|moderate|good|excellent,
  // score 0..100, reasons[]. Se mapea el nivel a color/etiqueta i18n.
  const HEALTH_META = useMemo(() => ({
    excellent: { color: "success", label: t("RouteHealthExcellent") || "Excelente" },
    good: { color: "info", label: t("RouteHealthGood") || "Buena" },
    moderate: { color: "warning", label: t("RouteHealthModerate") || "Moderada" },
    low: { color: "danger", label: t("RouteHealthLow") || "Baja" },
    critical: { color: "danger", label: t("RouteHealthCritical") || "Crítica" },
  }), [t])
  const routeHealth = useMemo(() => {
    // El backend expone la salud de ruta como campos PLANOS persistidos
    // (route_health_level / route_health_score). Se admite también el objeto
    // anidado route_health { level, score, reasons } del contrato/mock como
    // fallback.
    const rh = detail?.route_health
    const level = detail?.route_health_level ?? rh?.level ?? null
    const score = detail?.route_health_score ?? rh?.score ?? null
    const meta = (level && HEALTH_META[level]) || { color: "secondary", label: t("RouteHealthUnknown") || "Sin datos" }
    return {
      level,
      color: meta.color,
      label: meta.label,
      score,
      reasons: Array.isArray(rh?.reasons) ? rh.reasons : [],
    }
  }, [detail, HEALTH_META, t])

  // --- Trackings del contrato (polyline5) y selección de capa ---------------
  // Reglas (plan GRS §5c / U7):
  // Polyline5 decodificado (contrato previo). Se usa como geometría de respaldo
  // cuando el detalle aún no trae el `path[]` detallado del contrato GRS nuevo.
  const trackOriginal = useMemo(
    () => decodePolyline5(detail?.tracking_original), [detail],
  )
  const trackSanitized = useMemo(
    () => decodePolyline5(detail?.tracking_sanitized), [detail],
  )
  const trackPlanner = useMemo(
    () => decodePolyline5(detail?.tracking_from_planner), [detail],
  )
  // Manual = validado y NO automático (o reconstruido por el usuario).
  const isManual = validated && !autoValidated
  // ¿La ruta reconstruida es la principal? (validación manual con from_planner).
  const plannerOnly = isManual && trackPlanner.length > 0

  // --- Capas superpuestas del mapa (contrato GRS actualizado) ----------------
  // Cada ruta que exista se vuelve una capa: original, sanitizado, reconstruido.
  // Para el replay se prefiere el `path[]` (trae t/speed/heading); si no está,
  // se usa el polyline5 decodificado (solo geometría). La capa marcada `replay`
  // es sobre la que corre el recorrido animado: la reconstruida si el servicio
  // se validó manualmente, si no la sanitizada.
  const mapLayers = useMemo(() => {
    const layers = []
    const pushLayer = (id, label, color, path, pts, replay) => {
      const hasPath = Array.isArray(path) && path.length > 0
      const hasPts = Array.isArray(pts) && pts.length > 0
      if (!hasPath && !hasPts) return
      layers.push({
        id, label, color, replay,
        ...(hasPath ? { path } : { points: pts }),
      })
    }
    // La capa de replay preferente es la RECONSTRUIDA cuando GRS devolvió su
    // `path` (trae seq/tiempo/velocidad por punto). Esto vale aunque el servicio
    // aún no esté validado (reconstruir es previo a validar). Si no hay path de
    // reconstrucción, el replay cae en la sanitizada.
    const reconHasPath = Array.isArray(detail?.tracking_from_planner_path)
      && detail.tracking_from_planner_path.length > 0
    const sanitizedHasPath = Array.isArray(detail?.tracking_sanitized_path)
      && detail.tracking_sanitized_path.length > 0

    // Colores por capa (tokens design system Rombo):
    //  - Original (GPS crudo)   -> verde   (success  #34C38F)
    //  - Sanitizado             -> azul    (info     #539EF3)
    //  - Planeada/reconstruida  -> naranja (primary  #FF5F00, marca Rombo)
    // Reconstruida (from_planner): replay si tiene path (o es la ruta principal
    // de una validación manual).
    pushLayer(
      "reconstructed", t("TrackPlanner") || "Planeada", "#FF5F00",
      detail?.tracking_from_planner_path, trackPlanner, reconHasPath || plannerOnly,
    )
    // Sanitizada: replay solo si la reconstruida no es la fuente de replay.
    pushLayer(
      "sanitized", t("TrackSanitized") || "Sanitizada", "#539EF3",
      detail?.tracking_sanitized_path, trackSanitized,
      !reconHasPath && (sanitizedHasPath || !plannerOnly),
    )
    // Original (GPS crudo).
    pushLayer(
      "original", t("TrackOriginal") || "Original", "#34C38F",
      detail?.tracking_original_path, trackOriginal, false,
    )
    return layers
  }, [detail, trackPlanner, trackSanitized, trackOriginal, plannerOnly, t])
  const hasMapLayers = mapLayers.length > 0

  // Puntos de interés (ÚNICOS) del recorrido para el panel del mapa overlay.
  // Modelo: cada escala en pt_service_scale es un SEGMENTO (origin→destination),
  // y el destino de un segmento es el origen del siguiente. Los PUNTOS reales
  // del recorrido son, en orden:
  //   Origen (= origen del 1er segmento)
  //   Escala 1 (= destino del 1er segmento)
  //   Escala 2 (= destino del 2º segmento)
  //   ...
  //   Destino (= destino del último segmento)
  // Es decir: origen de la 1a escala + los DESTINOS de cada escala; el último
  // destino es el Destino del servicio. (No se listan los origin intermedios,
  // que duplicarían el punto anterior.)
  const mapPois = useMemo(() => {
    const pois = []
    const push = (kind, label, text, lat, lng) => {
      if (!hasPoint(lat, lng)) return
      pois.push({ kind, label, text: text || label, lat: Number(lat), lng: Number(lng) })
    }
    const orderedScales = [...(detail?.scales || [])].sort(
      (a, b) => (Number(a?.order ?? 0) - Number(b?.order ?? 0)),
    )
    if (orderedScales.length === 0) {
      // Sin escalas: usa origen/destino del servicio (fallback).
      push("start", t("Origin") || "Origen", originText, originLat, originLng)
      push("end", t("Destination") || "Destino", destinationText, destinationLat, destinationLng)
      return pois
    }
    // Origen del servicio = origen de la primera escala.
    const first = orderedScales[0]
    push("start", t("Origin") || "Origen", first.origin, first.origin_lat, first.origin_lng)
    // Puntos intermedios y destino: el DESTINO de cada escala. El último es el
    // Destino del servicio; los anteriores son "Escala N".
    orderedScales.forEach((sc, i) => {
      const isLast = i === orderedScales.length - 1
      if (isLast) {
        push("end", t("Destination") || "Destino", sc.destination, sc.destination_lat, sc.destination_lng)
      } else {
        push("scale", `${t("Scale") || "Escala"} ${i + 1}`, sc.destination, sc.destination_lat, sc.destination_lng)
      }
    })
    return pois
  }, [detail, originText, originLat, originLng, destinationText, destinationLat, destinationLng, t])

  // Reconstrucción de ruta (validación manual): re-consulta GRS para regenerar
  // el tracking (from_planner). Con MOCK recarga el detalle (vuelve a derivar
  // del contrato). En Onda 3 llamará al endpoint real de reconstrucción.
  // Reconstrucción de ruta (GRS modo B) sobre las escalas YA PERSISTIDAS. Se
  // ofrece en modo lectura, cuando ya hay escalas guardadas; durante la edición
  // el flujo es "Guardar y planear ruta" (saveEdit({thenRebuild:true})).
  const handleRebuildRoute = useCallback(async () => {
    setError(null); setSuccess(null); setRebuildingRoute(true)
    try {
      const token = await authHeader()
      await postServiceValidationRebuildRoute(token, code)
      await load()
      setSuccess(t("RouteRebuilt") || "Recorrido reconstruido")
    } catch (_e) {
      setError(t("RouteRebuildError") || "No se pudo reconstruir el recorrido")
    } finally {
      setRebuildingRoute(false)
    }
  }, [authHeader, code, load, t])

  // Actualizar direcciones: re-geocodifica (reverse) las escalas por sus
  // coordenadas (Mapbox->Google) y corrige direcciones invertidas. No toca
  // montos ni estado.
  const handleRegeocode = useCallback(async () => {
    setScaleToolsOpen(false)
    dismissScaleToolsNew()
    setError(null); setSuccess(null); setRegeocoding(true)
    try {
      const token = await authHeader()
      const res = await postServiceValidationRegeocode(token, code)
      await load()
      const n = res?.data?.scales_modified
      setSuccess(
        n != null
          ? `${t("AddressesUpdatedOk") || "Direcciones actualizadas"} (${n})`
          : (t("AddressesUpdatedOk") || "Direcciones actualizadas"),
      )
    } catch (e) {
      const status = e?.response?.status
      if (status === 409) setError(e?.response?.data?.detail || t("ValidationError"))
      else setError(e?.response?.data?.detail || t("AddressesUpdateError") || "No se pudieron actualizar las direcciones")
    } finally {
      setRegeocoding(false)
    }
  }, [authHeader, code, load, t])

  // Conteo de actividad del servicio a partir de la bitácora:
  // - Ediciones: versiones con action "edit" (edición estructural de escalas,
  //   registrada vía snapshot_and_log action="edit").
  // - Reconstrucciones: entradas del change log con action "route_rebuild"
  //   (reconstrucción de ruta GRS, registrada vía log_action).
  const activityCounts = useMemo(() => {
    const versions = audit?.versions || []
    const changes = audit?.changes || []
    const edits = versions.filter((v) => v.action === "edit").length
    const rebuilds = changes.filter((c) => c.action === "route_rebuild").length
    return { edits, rebuilds }
  }, [audit])

  // Tarjeta de actividad (totales de ediciones y reconstrucciones). Vive bajo el
  // mapa (columna derecha): resumen numérico rápido antes del detalle completo
  // de la bitácora.
  const activityCard = (
    <Card>
      <CardBody>
        <CardTitle tag="h5" className="mb-3">
          <i className="bx bx-history me-1" />{t("ServiceActivity") || "Actividad del servicio"}
        </CardTitle>
        <Row className="text-center">
          <Col xs={6}>
            <div className="border rounded p-3 h-100">
              <div className="fs-3 fw-bold text-primary">{activityCounts.edits}</div>
              <div className="text-muted small">{t("TotalEdits") || "Ediciones"}</div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="border rounded p-3 h-100">
              <div className="fs-3 fw-bold text-primary">{activityCounts.rebuilds}</div>
              <div className="text-muted small">{t("TotalRebuilds") || "Reconstrucciones"}</div>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )

  // Tarjeta de Bitácora (colapsable). Se declara aquí para poder ubicarla bajo
  // los tabs (columna izquierda) sin duplicar el markup.
  const auditCard = (
    <Card>
      <CardBody data-tour="svd-audit">
        <div className="d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }} onClick={() => setAuditOpen((v) => !v)}>
          <CardTitle tag="h5" className="mb-0">{t("AuditLog")}</CardTitle>
          <i className={`bx ${auditOpen ? "bx-chevron-up" : "bx-chevron-down"} fs-4`} />
        </div>
        {/* Resumen visible siempre: última acción (quién validó y resultado) */}
        {(audit.versions || []).length > 0 && (
          <div className="text-muted small mt-1">
            {(() => {
              const last = audit.versions[audit.versions.length - 1]
              return (
                <span>
                  {t("LastAction") || "Última acción"}: <strong>{actionLabel(last.action)}</strong>
                  {(last.created_by_name || last.created_by) ? ` · ${resolveUserName(last.created_by, last.created_by_name)}` : ""}
                  {last.created_at ? ` · ${fmtMxDateTime(last.created_at)}` : ""}
                </span>
              )
            })()}
          </div>
        )}
        <Collapse isOpen={auditOpen}>
          <div className="mt-3">
            {/* Desglose aritmetico de montos (PT-4070): se renderiza PRIMERO,
                antes de Versiones y ChangeLog. Consumo DEFENSIVO: el desglose
                (operaciones por componente y edicion manual) lo persiste el
                backend (PT-4069) en audit.amount_breakdown; si no viene,
                formatAmountBreakdown devuelve null y esta seccion no se
                renderiza (la bitacora no se rompe). */}
            {(() => {
              const breakdown = formatAmountBreakdown(audit?.amount_breakdown, t)
              if (!breakdown) return null
              const renderSide = (side, titleKey, titleFallback) => {
                if (!side) return null
                return (
                  <div className="mb-3">
                    <h6 className="text-muted">{t(titleKey) || titleFallback}</h6>
                    <Table size="sm" borderless className="mb-0">
                      <thead>
                        <tr>
                          <th>{t("Component") || "Componente"}</th>
                          <th>{t("Detail") || "Detalle"}</th>
                          <th className="text-end">{t("Amount") || "Monto"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {side.components.length === 0 ? (
                          <tr><td colSpan={3} className="text-center text-muted">{t("NoResults")}</td></tr>
                        ) : (
                          side.components.map((comp, i) => (
                            <tr key={comp.key || i}>
                              <td>{comp.label}</td>
                              <td className="text-muted small">{dash(comp.detail)}</td>
                              <td className="text-end">{comp.amount === null || comp.amount === undefined ? "—" : fmtMXN(comp.amount)}</td>
                            </tr>
                          ))
                        )}
                        {side.total !== null && side.total !== undefined && (
                          <tr>
                            <td className="fw-bold" colSpan={2}>{t("Total") || "Total"}</td>
                            <td className="text-end fw-bold">{fmtMXN(side.total)}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )
              }
              return (
                <div className="sv-amount-breakdown mb-3">
                  <h6 className="text-muted">
                    {t("AmountBreakdown") || "Desglose de montos"}
                    <Badge color="info" pill className="ms-1">{t("NewBadge") || "Nuevo"}</Badge>
                  </h6>
                  {/* Paso 1: edicion manual del monto va PRIMERO. */}
                  {breakdown.manualEdit && (
                    <div className="mb-3">
                      <div className="small">
                        <strong>{t("ManualAmountEdit") || "Edición manual de monto"}</strong>
                        {breakdown.manualEdit.field ? ` · ${fieldLabel(breakdown.manualEdit.field)}` : ""}
                      </div>
                      <div className="small text-muted">
                        {breakdown.manualEdit.old_value !== null && breakdown.manualEdit.old_value !== undefined
                          ? fmtMXN(breakdown.manualEdit.old_value)
                          : "—"}
                        {" → "}
                        {breakdown.manualEdit.new_value !== null && breakdown.manualEdit.new_value !== undefined
                          ? fmtMXN(breakdown.manualEdit.new_value)
                          : "—"}
                        {breakdown.manualEdit.changed_by_name ? ` · ${breakdown.manualEdit.changed_by_name}` : ""}
                        {breakdown.manualEdit.changed_at ? ` · ${fmtMxDateTime(breakdown.manualEdit.changed_at)}` : ""}
                      </div>
                    </div>
                  )}
                  {/* Operaciones aritmeticas: proveedor y luego cliente. */}
                  {renderSide(breakdown.supplier, "SupplierBreakdown", "Desglose proveedor")}
                  {renderSide(breakdown.customer, "CustomerBreakdown", "Desglose cliente")}
                </div>
              )
            })()}
            <h6 className="text-muted">{t("Versions")}</h6>
            <Table size="sm" className="mb-3">
              <thead>
                <tr><th>#</th><th>{t("Action")}</th><th>{t("User")}</th><th>{t("Date")}</th></tr>
              </thead>
              <tbody>
                {(audit.versions || []).length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-muted">{t("NoResults")}</td></tr>
                ) : (
                  audit.versions.map((v) => (
                    <tr key={v.version_no}>
                      <td>{v.version_no}{v.is_baseline ? " (base)" : ""}</td>
                      <td>{actionLabel(v.action)}</td>
                      <td>{dash(resolveUserName(v.created_by, v.created_by_name))}</td><td>{v.created_at ? fmtMxDateTime(v.created_at) : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <h6 className="text-muted">{t("ChangeLog")}</h6>
            <Table size="sm" className="mb-0">
              <thead>
                <tr><th>{t("Action") || "Acción"}</th><th>{t("Field")}</th><th>{t("Before")}</th><th>{t("After")}</th><th>{t("User")}</th></tr>
              </thead>
              <tbody>
                {(audit.changes || []).length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted">{t("NoResults")}</td></tr>
                ) : (
                  audit.changes.map((c, i) => (
                    <tr key={i}>
                      <td>{actionLabel(c.action)}</td>
                      <td>{fieldLabel(c.field)}</td>
                      <td>{formatFieldValue(c.field, c.old_value, t, (sub) => resolveUserName(sub))}</td>
                      <td>{formatFieldValue(c.field, c.new_value, t, (sub) => resolveUserName(sub))}</td>
                      <td>{dash(resolveUserName(c.changed_by, c.changed_by_name))}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Collapse>
      </CardBody>
    </Card>
  )

  // Tarjeta del mapa (Recorrido). Vive en la columna derecha bajo Montos y es
  // visible siempre (menos clics: no hay que abrir un tab para ver la ruta).
  // Usa ServiceRouteMap (MapLibre): recorrido + velocidad (color) + marcadores
  // de distancia + capas base. El clic en origen/destino/escala centra el mapa
  // (flyTo directo, sin recargar). Al enfocar crece de 360 a 560px.
  const mapCard = (
    <Card innerRef={mapCardRef}>
      <CardBody data-tour="svd-map">
        <div className="d-flex justify-content-between align-items-center gap-2 mb-2 flex-wrap">
          <CardTitle tag="h5" className="mb-0">
            <i className="bx bx-map-alt me-1" />{t("Route") || "Recorrido"}
          </CardTitle>
          <div className="d-flex align-items-center gap-2 ms-auto">
            {focusPoint?.label && (
              <span className="badge bg-light text-body border text-truncate" style={{ maxWidth: 160 }}>
                <i className="bx bx-map-pin me-1" />{focusPoint.label}
              </span>
            )}
            {/* A2: toggle de direcciones originales bajo los geopuntos. Solo se
                muestra cuando hay un overlay de desviación activo. */}
            {deviationOverlay && (
              <div className="form-check form-switch mb-0 d-flex align-items-center">
                <Input type="switch" role="switch" id="svc-show-dev-addresses"
                  className="me-1"
                  checked={showDeviationAddresses}
                  onChange={(e) => setShowDeviationAddresses(e.target.checked)} />
                <Label for="svc-show-dev-addresses" className="form-check-label small mb-0"
                  style={{ cursor: "pointer" }}>
                  {t("ShowOriginalAddresses") || "Mostrar direcciones"}
                </Label>
              </div>
            )}
            {hasMapLayers && (
              <Button color="light" size="sm" className="flex-shrink-0"
                onClick={() => setMapExpanded((v) => !v)}
                title={mapExpanded ? (t("Collapse") || "Reducir") : (t("Expand") || "Ampliar")}>
                <i className={`bx ${mapExpanded ? "bx-exit-fullscreen" : "bx-fullscreen"} me-1`} />
                {mapExpanded ? (t("Collapse") || "Reducir") : (t("Expand") || "Ampliar")}
              </Button>
            )}
          </div>
        </div>

        {/* Nota de contexto: cuando es validación manual con ruta reconstruida,
            esa capa es la principal (replay). El resto se superpone y puede
            ocultarse con los toggles del propio mapa. */}
        {plannerOnly && (
          <div className="mb-2">
            <Badge color="info" className="d-inline-flex align-items-center gap-1">
              <i className="bx bx-git-repo-forked" />{t("TrackPlanner") || "Ruta reconstruida"}
            </Badge>
            <small className="text-muted ms-2">{t("TrackPlannerOnlyHint")}</small>
          </div>
        )}

        {hasMapLayers ? (
          <div className="gpx-map-container"
            style={{ height: mapExpanded ? "560px" : "360px", position: "relative", width: "100%", transition: "height .25s ease" }}>
            <ServiceRouteMap
              layers={mapLayers}
              focusPoint={focusPoint}
              deviationOverlay={deviationOverlay}
              showDeviationAddresses={showDeviationAddresses}
              features={{ route: true, speed: false, distanceMarkers: false, basemaps: true, elevation: false, stats: true, export: true, replay: true }}
              height="100%"
              serviceCode={code}
              onRequestFullscreen={() => setMapFullscreen(true)} />
          </div>
        ) : tripStatus === "FINISHED" && urlImage ? (
          <div style={{ height: "280px", overflow: "hidden" }}>
            <img src={urlImage} alt="Route"
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        ) : (
          <div style={{
            height: "300px", display: "flex", justifyContent: "center",
            alignItems: "center", backgroundColor: "#F8F9FA", borderRadius: "8px",
          }}>
            <MapStatusMessage status={tripStatus} />
          </div>
        )}
      </CardBody>
    </Card>
  )

  if (!canRead) {
    return (
      <div className="page-content">
        <Container fluid>
          <span className="error-message-requests">
            <i className="fa fa-exclamation-circle" /> {t("NoPermissionsViewServices")}
          </span>
        </Container>
      </div>
    )
  }

  // Servicio cancelado (Trip en DELETED): el indicador de cancelado tiene
  // prioridad sobre validado/por validar, igual que el ribbon del ERP.
  const statusChip = isCancelled ? (
    <Badge color="danger" className="sv-status-chip">
      <i className="bx bx-block" />
      {t("Cancelled") || "Cancelado"}
    </Badge>
  ) : (
    <Badge color={validated ? "success" : "warning"} className="sv-status-chip">
      <i className={`bx ${validated ? "bx-check-shield" : "bx-time-five"}`} />
      {validated ? t("Validated") : t("ToValidate")}
    </Badge>
  )

  return (
    <div className="page-content">
      <Container fluid>
        {/* Encabezado con estado y CTA principal a 1 clic */}
        {/* Breadcrumb interactivo: vuelve a la vista (lista/kanban) con filtros */}
        <Row className="mb-2">
          <Col xs={12}>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={returnUrl}>{t("Services")}</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={returnUrl}>{t("ServiceValidation")} · {returnView}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">{code}</li>
              </ol>
            </nav>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col xs={12} className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-3">
              {/* Atrás: si hay servicio anterior en la secuencia, regresa a él;
                  si no, vuelve a la vista (lista/kanban). Para volver siempre a
                  la lista está el breadcrumb de arriba. */}
              <Button className="sv-nav-btn sv-nav-btn-icon"
                onClick={() => navigate(prevCode ? `/service-validation/${prevCode}` : returnUrl)}
                aria-label={prevCode
                  ? (t("PreviousService") || "Servicio anterior")
                  : (t("BackToList") || t("Back") || "Regresar a la lista")}
                title={prevCode
                  ? (t("PreviousService") || "Servicio anterior")
                  : (t("BackToList") || t("Back") || "Regresar a la lista")}>
                <i className="bx bx-left-arrow-circle" />
              </Button>
              <div className="d-flex flex-column">
                <h4 className="mb-0 lh-1">
                  {code}
                  {listPosition && (
                    <small className="sv-list-counter text-muted ms-2"
                      title={t("PositionInList") || "Posición en la lista"}>
                      {listPosition.index}/{listPosition.total}
                    </small>
                  )}
                </h4>
                <div className="mt-1">{statusChip}</div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2" data-tour="svd-actions">
              {/* On-hold: tomar/liberar el servicio en exclusiva. Oculto si ya
                  está validado. */}
              {canWrite && !validated && (
                heldByMe ? (
                  <Button color="outline-secondary" className="sv-btn-icon-text" onClick={handleRelease} disabled={acting}
                    title={t("ReleaseHold") || "Liberar"}>
                    {acting ? <Spinner size="sm" /> : <><i className="bx bx-lock-open-alt me-1" />{t("ReleaseHold") || "Liberar"}</>}
                  </Button>
                ) : (
                  <Button color="outline-primary"
                    className={`sv-btn-icon-text${!onholdBy && !isCancelled ? " sv-cta-pulse" : ""}`}
                    onClick={handleHold} disabled={acting || heldByOther || isCancelled}
                    title={isCancelled
                      ? (t("ServiceCancelledNoActions") || "El servicio está cancelado")
                      : (heldByOther ? `${t("HeldByOther") || "Tomado por otro usuario"}: ${onholdByName}` : (t("TakeHold") || "Tomar"))}>
                    {acting ? <Spinner size="sm" /> : <><i className="bx bx-lock-alt me-1" />{t("TakeHold") || "Tomar"}</>}
                  </Button>
                )
              )}
              {/* El CTA Validar se reubicó al panel lateral "Montos del
                  servicio" (junto a Calcular/Editar). Aquí solo permanece
                  Invalidar (cuando ya está validado) y el dropdown de opciones. */}
              {canValidate && validated && (
                <Button color="outline-danger" className="sv-btn-icon-text" onClick={handleUnvalidate} disabled={acting}>
                  {acting ? <Spinner size="sm" /> : <><i className="bx bx-x-circle me-1" />{t("Unvalidate")}</>}
                </Button>
              )}
              {/* Menú de opciones: agrupa Comparación de versiones y Ver tutorial. */}
              <ButtonDropdown isOpen={optionsOpen} toggle={() => setOptionsOpen((v) => !v)}>
                <DropdownToggle className="sv-options-dropdown sv-options-dropdown-lg" caret
                  title={t("Options") || "Opciones"} aria-label={t("Options") || "Opciones"}>
                  <i className="bx bx-dots-vertical-rounded" />
                </DropdownToggle>
                <DropdownMenu end className="custom-dropdown-menu">
                  {canValidate && !validated && (
                    <DropdownItem onClick={handleValidateZero} disabled={!canDoValidate || acting}>
                      <i className="bx bx-dollar-circle me-2" />
                      {t("ValidateZero") || "Validar en 0s"}
                    </DropdownItem>
                  )}
                  {/* Cierre sin cliente (PT-4066/PT-4074): dispara el overlay de
                      tipo de cierre. Ahora vive en el dropdown de acciones. */}
                  {canValidate && !validated && (
                    <DropdownItem data-tour="svd-close-no-customer"
                      onClick={() => { setCwcClose(""); setCloseNoCustomerOpen(true) }}
                      disabled={!canDoValidate || acting}>
                      <i className="bx bx-user-x me-2" />
                      {t("CloseWithoutCustomer")}
                    </DropdownItem>
                  )}
                  {/* Cancelacion de servicio (PT-4074): mismas guardas que el
                      boton original; dispara el modal de confirmacion. */}
                  {canValidate && !validated && (
                    <DropdownItem onClick={() => { setCancelReason(""); setCancelOpen(true) }}
                      disabled={acting || heldByOther || !heldByMe || isCancelled}>
                      <i className="bx bx-block me-2" />
                      {t("CancelService") || "Cancelar servicio"}
                    </DropdownItem>
                  )}
                  <DropdownItem onClick={() => navigate(`/service-validation/${code}/diff`)}>
                    <i className="bx bx-git-compare me-2" />
                    {t("SnapshotDiff") || "Diff"}
                  </DropdownItem>
                  <DropdownItem onClick={() => setTourOpen(true)}>
                    <i className="bx bx-help-circle me-2" />
                    {t("ViewTutorial") || "Ver tutorial"}
                  </DropdownItem>
                </DropdownMenu>
              </ButtonDropdown>
              {/* Siguiente servicio por validar (respeta el orden de la vista) */}
              {nextCode && (
                <Button className="sv-nav-btn sv-nav-btn-icon"
                  onClick={() => navigate(`/service-validation/${nextCode}`)}
                  aria-label={t("NextService") || "Siguiente por validar"}
                  title={t("NextService") || "Siguiente por validar"}>
                  <i className="bx bx-right-arrow-circle" />
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {validateBlockedReason && !validated && (
          <Alert color="warning" className="py-2">
            <i className="bx bx-lock-alt me-1" /> {validateBlockedReason}
          </Alert>
        )}
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
        {success && <Alert color="success" toggle={() => setSuccess(null)}>{success}</Alert>}

        {isLoading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <Row>
            {/* IZQUIERDA: datos del servicio (principal + tabs) */}
            <Col lg={8}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <CardTitle tag="h5">{t("ServiceData") || "Datos del servicio"}</CardTitle>
                    {/* Indicador de salud de la ruta (contrato GRS: nivel + score). */}
                    <Badge color={routeHealth.color} className="sv-health-chip d-inline-flex align-items-center gap-1 px-2 py-1 mb-2"
                      title={routeHealth.reasons.length ? routeHealth.reasons.join(" · ") : undefined}>
                      <i className="bx bx-pulse" />
                      <span>
                        {t("RouteHealth") || "Salud de ruta"}: {routeHealth.label}
                        {routeHealth.score != null ? ` (${routeHealth.score})` : ""}
                      </span>
                    </Badge>
                  </div>

                  {/* Fechas clave destacadas: solicitud / inicio / fin + plaza */}
                  <Row className="g-2 mb-3">
                    {[
                      { k: "requested", label: t("RequestedAt") || "Solicitud", icon: "bx-calendar-plus", val: requestedAt },
                      { k: "started", label: t("StartedAt") || "Inicio", icon: "bx-play-circle", val: startedAt },
                      { k: "ended", label: t("EndedAt") || "Finalización", icon: "bx-flag", val: endedAt },
                    ].map((it) => (
                      <Col xs={12} md={3} key={it.k}>
                        <div className="border rounded p-2 h-100 bg-light">
                          <div className="text-muted small d-flex align-items-center gap-1">
                            <i className={`bx ${it.icon}`} /> {it.label}
                          </div>
                          <div className="fw-semibold">{fmtMxDateTime(it.val)}</div>
                        </div>
                      </Col>
                    ))}
                    {/* Plaza del servicio, al lado de Finalización. El backend la
                        expone como id numérico (detail.trip.place / detail.place)
                        mapeable con listPlaces(); '—' si no viene. */}
                    <Col xs={12} md={3}>
                      <div className="border rounded p-2 h-100 bg-light">
                        <div className="text-muted small d-flex align-items-center gap-1">
                          <i className="bx bx-map" /> {t("Place")}
                        </div>
                        <div className="fw-semibold">{placeText ? placeText : "—"}</div>
                      </div>
                    </Col>
                  </Row>

                  {/* Sección principal tabular (datos prioritarios, estilo ERP) */}
                  <Row>
                    <Col md={6}>
                      <KV label={t("TypeService")}>{dash(t(tripService?.type) || tripService?.type)}</KV>
                      <KV label={t("Customer")}>
                        <span className="fw-bold text-dark">{dash(companyName)}</span>
                      </KV>
                      <KV label={t("RequestedBy")}>{dash(requestedByName)}</KV>
                      <KV label={t("Driver")}>{dash(driverName)}</KV>
                      <KV label={t("Vehicle")}>{dash(vehicleDesc)}</KV>
                      <KV label={t("Plate")}>{dash(plate)}</KV>
                      {/* Flags/checks de estado (PT-4070): reorganizados en 2
                          columnas. IZQUIERDA: Cierre sin cliente / Ruta
                          planificada / Documentación pendiente. DERECHA:
                          Validación automática / Validado / Corregido
                          manualmente. Lógica intacta; solo cambia el layout. */}
                      <Row className="mt-2" data-tour="svd-checks">
                        <Col xs={6}>
                          {/* Cierre sin cliente: del billing (close_without_customer) o,
                              como respaldo para baselines antiguos, del Trip
                              (is_without_passengers). */}
                          <Flag label={t("CloseWithoutCustomer") || "Cierre sin cliente"}
                            on={!!detail?.close_without_customer || !!dt?.is_without_passengers} />
                          <Flag label={t("RoutePlanned") || "Ruta pre calculada"} on={!!isRoutePlanned} />
                          <Flag label={t("PendingDocumentation") || "Documentación pendiente"} on={!!detail?.pending_documentation} />
                        </Col>
                        <Col xs={6}>
                          {/* Validación automática: resaltada cuando aplica. */}
                          <div className="d-flex align-items-center gap-2 py-1">
                            <i className={`bx ${autoValidated ? "bx-check-circle text-success" : "bx-circle text-muted"}`} />
                            {autoValidated ? (
                              <span className="fw-bold text-success">
                                {t("AutoValidation") || "Validación automática"}
                                <Badge color="success" className="ms-2">{t("Yes") || "Sí"}</Badge>
                              </span>
                            ) : (
                              <span className="text-muted">{t("AutoValidation") || "Validación automática"}</span>
                            )}
                          </div>
                          <Flag label={t("Validated")} on={validated} />
                          <Flag label={t("ManuallyCorrected") || "Corregido manualmente"} on={!!detail?.manually_corrected} />
                        </Col>
                      </Row>
                      {/* On-hold: quién tomó el servicio (si hay hold vigente). */}
                      {onholdBy && (
                        <KV label={t("OnHoldBy") || "Tomado por"}>
                          <span className={heldByMe ? "text-success" : "text-warning"}>
                            <i className="bx bx-lock-alt me-1" />
                            {heldByMe ? (t("You") || "Tú") : onholdByName}
                          </span>
                        </KV>
                      )}
                      {/* Tiempo de validación (si ya validado y medible). */}
                      {validated && detail?.validation_time_seconds != null && (
                        <KV label={t("ValidationTime") || "Tiempo de validación"}>
                          {(() => {
                            const s = Number(detail.validation_time_seconds)
                            if (!Number.isFinite(s)) return dash(null)
                            const h = Math.floor(s / 3600)
                            const m = Math.floor((s % 3600) / 60)
                            const sec = s % 60
                            return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${sec}s` : `${sec}s`
                          })()}
                        </KV>
                      )}
                    </Col>
                    <Col md={6}>
                      <KV label={t("Origin")}>
                        {hasPoint(originLat, originLng) ? (
                          <>
                            <button type="button" className="btn btn-link p-0 text-end text-decoration-none"
                              onClick={() => focusOnMap(originLat, originLng, originText)}
                              title={t("ShowOnMap") || "Ver en el mapa"}>
                              {dash(originText)} <i className="bx bx-map-pin text-info" />
                            </button>
                            <CopyCoordsButton lat={originLat} lng={originLng}
                              title={t("CopyCoords") || "Copiar lat,lng"} />
                          </>
                        ) : dash(originText)}
                      </KV>
                      <KV label={t("Destination")}>
                        {hasPoint(destinationLat, destinationLng) ? (
                          <>
                            <button type="button" className="btn btn-link p-0 text-end text-decoration-none"
                              onClick={() => focusOnMap(destinationLat, destinationLng, destinationText)}
                              title={t("ShowOnMap") || "Ver en el mapa"}>
                              {dash(destinationText)} <i className="bx bx-map-pin text-info" />
                            </button>
                            <CopyCoordsButton lat={destinationLat} lng={destinationLng}
                              title={t("CopyCoords") || "Copiar lat,lng"} />
                          </>
                        ) : dash(destinationText)}
                      </KV>
                      <KV label={t("Mileage")}>
                        {(() => {
                          // Kilometraje sin decimales (0.5 -> arriba). El backend
                          // ya lo redondea; aquí se asegura la presentación entera.
                          const km = detail?.kms ?? dt.kms ?? legacyTrip?.distance
                          return km != null && km !== "" && Number.isFinite(Number(km))
                            ? Math.round(Number(km))
                            : dash(km)
                        })()}
                      </KV>
                      <KV label={`${t("Duration")} (m)`}>
                        {(() => {
                          // La duración del servicio viene en segundos; se muestra
                          // en minutos redondeados.
                          const d = detail?.duration ?? dt.duration ?? legacyTrip?.time
                          return d != null && d !== "" && Number.isFinite(Number(d))
                            ? Math.round(Number(d) / 60)
                            : dash(d)
                        })()}
                      </KV>
                      <KV label={t("UnitType") || "Tipo de unidad"}>{dash(dt.vehicle_type)}</KV>
                      <KV label={t("Status")}>{dash(dt.status_label || t(`TripStatus_${tripStatus}`) || tripStatus)}</KV>
                    </Col>
                  </Row>

                  {/* Notas: siempre de solo lectura. El valor se carga del
                      detalle y viaja intacto en los payloads de validar/editar,
                      pero no se edita desde esta vista. */}
                  <FormGroup className="mt-3 mb-0" data-tour="svd-notes">
                    <Label className="mb-1">{t("Notes")}</Label>
                    <Input type="textarea" rows={2} value={notes} readOnly />
                  </FormGroup>

                  {/* Nota de cierre: motivo capturado al finalizar el servicio
                      (Trip.finished_notes). Solo lectura; se muestra cuando existe. */}
                  {dt?.finished_notes && (
                    <FormGroup className="mt-3 mb-0">
                      <Label className="mb-1">{t("CloseNote") || "Nota de cierre"}</Label>
                      <Input type="textarea" rows={2} value={dt.finished_notes} readOnly />
                    </FormGroup>
                  )}
                </CardBody>
              </Card>

              {/* Sub-secciones del servicio: Escalas / Evidencias / Pasajeros /
                  Timeline. Se eliminó el stepper de 2 pasos: el flujo de montos
                  (Calcular / Editar / Validar) se movió al panel lateral "Montos
                  del servicio" y el tab "Montos" dejó de existir. */}
              <Card>
                <CardBody>
                      <Nav tabs className="mb-3" data-tour="svd-tabs">
                        <NavItem>
                          <NavLink className={classnames({ active: panel === "scales" })}
                            onClick={() => setPanel("scales")} style={{ cursor: "pointer" }}>
                            <i className="bx bx-map-pin me-1" />{t("Scales")}
                            {(detail?.scales || []).length > 0 && (
                              <Badge color="light" className="ms-1 text-dark">{(detail?.scales || []).length}</Badge>
                            )}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({ active: panel === "pods", disabled: podsApprovalExempt })}
                            onClick={() => { if (!podsApprovalExempt) setPanel("pods") }}
                            aria-disabled={podsApprovalExempt}
                            style={{
                              cursor: podsApprovalExempt ? "not-allowed" : "pointer",
                              opacity: podsApprovalExempt ? 0.5 : 1,
                            }}>
                            <i className="bx bx-receipt me-1" />{t("Evidence") || "Evidencias"}
                            {!podsApprovalExempt && pendingPods > 0 && (
                              <Badge color="warning" className="ms-1">{pendingPods}</Badge>
                            )}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink className={classnames({ active: panel === "riders" })}
                            onClick={() => setPanel("riders")} style={{ cursor: "pointer" }}>
                            <i className="bx bx-group me-1" />{t("Passengers")}
                            {riders.length > 0 && (
                              <Badge color="light" className="ms-1 text-dark">{riders.length}</Badge>
                            )}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink className={classnames({ active: panel === "timeline" })}
                            onClick={() => setPanel("timeline")} style={{ cursor: "pointer" }}>
                            <i className="bx bx-time me-1" />{t("Timeline") || "Timeline"}
                            {timeline.length > 0 && (
                              <Badge color="light" className="ms-1 text-dark">{timeline.length}</Badge>
                            )}
                          </NavLink>
                        </NavItem>
                      </Nav>

                      <TabContent activeTab={panel}>
                        {/* Escalas */}
                        <TabPane tabId="scales">
                      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                        {/* Hint: edición bloqueada hasta tomar el servicio. */}
                        {canWrite && !validated && !editMode && !heldByMe && (
                          <small className="text-muted me-auto">
                            <i className="bx bx-lock-alt me-1" />
                            {heldByOther
                              ? `${t("HeldByOther") || "Tomado por otro usuario"}: ${onholdByName}`
                              : (t("TakeToEditHint") || "Toma el servicio para editar las escalas.")}
                          </small>
                        )}
                        {canEditScales && !editMode && (detail?.scales || []).length > 0 && (
                          <ButtonDropdown isOpen={scaleToolsOpen}
                            toggle={() => { setScaleToolsOpen((v) => !v); dismissScaleToolsNew() }}
                            data-tour="svd-scale-tools">
                            <DropdownToggle color="light" size="sm" caret
                              className="position-relative"
                              disabled={rebuildingRoute || regeocoding}
                              title={t("ScaleTools") || "Herramientas de escalas"}>
                              {(rebuildingRoute || regeocoding)
                                ? <Spinner size="sm" />
                                : <><i className="bx bx-wrench me-1" />{t("ScaleTools") || "Herramientas"}</>}
                              {scaleToolsNew && (
                                <Badge color="danger" pill
                                  className="position-absolute top-0 start-100 translate-middle"
                                  style={{ fontSize: "0.6rem" }}>
                                  {t("New") || "Nuevo"}
                                </Badge>
                              )}
                            </DropdownToggle>
                            <DropdownMenu end className="custom-dropdown-menu">
                              <DropdownItem onClick={handleRegeocode} disabled={regeocoding}>
                                <i className="bx bx-map-pin me-2" />
                                {t("UpdateAddresses") || "Actualizar direcciones"}
                              </DropdownItem>
                              <DropdownItem onClick={handleRebuildRoute} disabled={rebuildingRoute}
                                data-tour="svd-rebuild">
                                <i className="bx bx-git-repo-forked me-2" />
                                {t("RebuildRoute") || "Reconstrucción de ruta"}
                              </DropdownItem>
                            </DropdownMenu>
                          </ButtonDropdown>
                        )}
                        {canEditScales && !editMode && (
                          <Button color="light" size="sm" onClick={startEdit} data-tour="svd-edit-scales">
                            <i className="bx bx-edit me-1" /> {t("EditScales")}
                          </Button>
                        )}
                      </div>
                      {!editMode ? (
                        <Table className="table table-bordered mb-0" size="sm" responsive>
                          <thead>
                            <tr>
                              <th>{t("Origin")}</th><th>{t("Destination")}</th>
                              <th>{t("Passenger") || "Pasajero"}</th>
                              <th>{t("CostCenter") || "Centro de costos"}</th>
                              <th>{t("StartTime") || "Hora inicio"}</th>
                              <th>{t("EndTime") || "Hora fin"}</th>
                              <th>Kms</th><th>{`${t("Duration")} (m)`}</th>
                              <th>{t("SupplierAmount")}</th><th>{t("CustomerAmount")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail?.scales || []).length === 0 ? (
                              <tr><td colSpan={10} className="text-center text-muted">{t("NoResults")}</td></tr>
                            ) : (
                              detail.scales.map((sc, i) => {
                                const roExpanded = expandedRows.has(i)
                                return (
                                <React.Fragment key={sc.external_id || i}>
                                <tr>
                                  <td>
                                    <div className="d-flex align-items-start gap-1">
                                      {/* Ver dirección geolocalizada (operador) vs
                                          configurada SIN entrar en edición (row colapsable). */}
                                      <button type="button"
                                        className="btn btn-link p-0 text-muted d-inline-flex mt-1"
                                        onClick={() => toggleScaleOriginal(i)}
                                        aria-expanded={roExpanded}
                                        title={t("ShowOriginalAddress") || "Ver dirección original"}>
                                        <i className={`bx ${roExpanded ? "bx-chevron-up" : "bx-chevron-down"}`} />
                                      </button>
                                      <div>
                                        {hasPoint(sc.origin_lat, sc.origin_lng) ? (
                                          <>
                                            <button type="button" className="btn btn-link p-0 text-decoration-none"
                                              onClick={() => focusOnMap(sc.origin_lat, sc.origin_lng, sc.origin)}
                                              title={sc.origin || (t("ShowOnMap") || "Ver en el mapa")}>
                                              {ellipsis(sc.origin)} <i className="bx bx-map-pin text-info" />
                                            </button>
                                            <CopyCoordsButton lat={sc.origin_lat} lng={sc.origin_lng}
                                              title={t("CopyCoords") || "Copiar lat,lng"} />
                                          </>
                                        ) : ellipsis(sc.origin)}
                                        {renderDeviation(sc, "origin")}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    {hasPoint(sc.destination_lat, sc.destination_lng) ? (
                                      <>
                                        <button type="button" className="btn btn-link p-0 text-decoration-none"
                                          onClick={() => focusOnMap(sc.destination_lat, sc.destination_lng, sc.destination)}
                                          title={sc.destination || (t("ShowOnMap") || "Ver en el mapa")}>
                                          {ellipsis(sc.destination)} <i className="bx bx-map-pin text-info" />
                                        </button>
                                        <CopyCoordsButton lat={sc.destination_lat} lng={sc.destination_lng}
                                          title={t("CopyCoords") || "Copiar lat,lng"} />
                                      </>
                                    ) : ellipsis(sc.destination)}
                                    {renderDeviation(sc, "destination")}
                                  </td>
                                  <td>{ellipsis(sc.passenger)}</td>
                                  <td>{ellipsis(sc.cost_center)}</td>
                                  <td>{sc.start_time ? fmtMxDateTime(sc.start_time) : "—"}</td>
                                  <td>{sc.end_time ? fmtMxDateTime(sc.end_time) : "—"}</td>
                                  {/* Kms entero (redondeado). */}
                                  <td>{sc.kms != null ? Math.round(Number(sc.kms)) : "—"}</td>
                                  {/* Duración en minutos, entero (redondeado). */}
                                  <td>{sc.duration != null ? Math.round(Number(sc.duration) / 60) : "—"}</td>
                                  <td className="text-end">{fmtMXN(sc.amount)}</td>
                                  <td className="text-end">{fmtMXN(sc.customer_amount)}</td>
                                </tr>
                                {/* Fila colapsable (solo lectura): dirección CONFIGURADA
                                    (original de creación) y dirección GEOLOCALIZADA
                                    registrada por el operador, por extremo origen/destino. */}
                                <tr className="svc-scale-original-row">
                                  <td colSpan={10} className="p-0 border-0">
                                    <Collapse isOpen={roExpanded}>
                                      <div className="px-3 py-2 bg-light small">
                                        <Row>
                                          <Col md={6}>
                                            <div className="text-muted fw-bold mb-1">
                                              <i className="bx bx-flag me-1" />{t("Origin")}
                                            </div>
                                            <div>
                                              <span className="text-muted">{t("ConfiguredAddress") || "Dirección configurada"}: </span>
                                              {sc.origin || "—"}
                                            </div>
                                            <div>
                                              <span className="text-muted">{t("GeolocatedAddress") || "Dirección geolocalizada (operador)"}: </span>
                                              {sc.operator_origin
                                                || (hasPoint(sc.operator_origin_lat, sc.operator_origin_lng)
                                                  ? `${Number(sc.operator_origin_lat).toFixed(6)}, ${Number(sc.operator_origin_lng).toFixed(6)}`
                                                  : "—")}
                                            </div>
                                          </Col>
                                          <Col md={6}>
                                            <div className="text-muted fw-bold mb-1">
                                              <i className="bx bx-flag-checkered me-1" />{t("Destination")}
                                            </div>
                                            <div>
                                              <span className="text-muted">{t("ConfiguredAddress") || "Dirección configurada"}: </span>
                                              {sc.destination || "—"}
                                            </div>
                                            <div>
                                              <span className="text-muted">{t("GeolocatedAddress") || "Dirección geolocalizada (operador)"}: </span>
                                              {sc.operator_destination
                                                || (hasPoint(sc.operator_destination_lat, sc.operator_destination_lng)
                                                  ? `${Number(sc.operator_destination_lat).toFixed(6)}, ${Number(sc.operator_destination_lng).toFixed(6)}`
                                                  : "—")}
                                            </div>
                                          </Col>
                                        </Row>
                                      </div>
                                    </Collapse>
                                  </td>
                                </tr>
                                </React.Fragment>
                                )
                              })
                            )}
                          </tbody>
                          {(detail?.scales || []).length > 0 && (
                            <tfoot>
                              <tr className="fw-bold table-light">
                                <td colSpan={6} className="text-end">{t("Total") || "Total"}</td>
                                <td>{Math.round(detail.scales.reduce((a, s) => a + (Number(s.kms) || 0), 0))}</td>
                                <td>{Math.round(detail.scales.reduce((a, s) => a + (Number(s.duration) || 0), 0) / 60)}</td>
                                <td className="text-end">{fmtMXN(detail.scales.reduce((a, s) => a + (Number(s.amount) || 0), 0))}</td>
                                <td className="text-end">{fmtMXN(detail.scales.reduce((a, s) => a + (Number(s.customer_amount) || 0), 0))}</td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      ) : (
                        <>
                          {/* Las escalas se reordenan ÚNICAMENTE arrastrando la fila
                              por el handle (drag&drop). El orden efectivo se reasigna
                              al soltar (ver onScalesDragEnd). */}
                          <DragDropContext onDragEnd={onScalesDragEnd}>
                            <Table className="table table-bordered mb-2" size="sm" responsive>
                              <thead>
                                <tr>
                                  <th style={{ width: 40 }} aria-label={t("Order") || "Orden"} />
                                  <th>{t("Origin")}</th><th>{t("Destination")}</th>
                                  <th style={{ width: 100, maxWidth: 100 }}>{t("Passenger") || "Pasajero"}</th>
                                  <th style={{ width: 100, maxWidth: 100, whiteSpace: "normal" }}>{t("CostCenter") || "Centro de costos"}</th>
                                  <th>{t("StartTime") || "Hora inicio"}</th>
                                  <th>{t("EndTime") || "Hora fin"}</th>
                                  <th style={{ width: 90, minWidth: 80 }}>Kms</th>
                                  <th style={{ width: 110 }}>{`${t("Duration")} (m)`}</th><th></th>
                                </tr>
                              </thead>
                              <Droppable droppableId="edit-scales">
                                {(dropProvided) => (
                                  <tbody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                    {editScales.length === 0 ? (
                                      <tr><td colSpan={10} className="text-center text-muted">{t("NoResults")}</td></tr>
                                    ) : (
                                      (() => {
                                        // Reordenamiento solo por drag&drop (sin índice ni
                                        // botones subir/bajar): no se requieren posiciones.
                                        return editScales.map((s, i) => {
                                          const rowKey = s.uuid || s.external_id || `new-${i}`
                                          return (
                                            <Draggable key={rowKey} draggableId={String(rowKey)} index={i} isDragDisabled={s.deleted}>
                                              {(dragProvided, snapshot) => {
                                                const isNewScale = !s.uuid
                                                const expanded = expandedRows.has(i)
                                                return (
                                                <React.Fragment>
                                                <tr
                                                  ref={dragProvided.innerRef}
                                                  {...dragProvided.draggableProps}
                                                  className={s.deleted ? "table-danger" : (snapshot.isDragging ? "table-active" : "")}
                                                  style={{ ...dragProvided.draggableProps.style }}
                                                >
                                                  <td style={{ width: 40 }}>
                                                    {/* Reordenar: solo drag&drop. El handle de arrastre
                                                        es el único control (sin índice ni botones subir/bajar). */}
                                                    <div className="d-flex align-items-center gap-1">
                                                      <span {...dragProvided.dragHandleProps}
                                                        className="text-muted d-inline-flex"
                                                        style={{ cursor: s.deleted ? "not-allowed" : "grab" }}
                                                        title={t("DragToReorder") || "Arrastrar para reordenar"}>
                                                        <i className="bx bx-dots-vertical-rounded" />
                                                      </span>
                                                      {/* A3: ver dirección original (creación). Oculto en
                                                          filas borradas. */}
                                                      {!s.deleted && (
                                                        <button type="button"
                                                          className="btn btn-link p-0 text-muted d-inline-flex"
                                                          onClick={() => toggleScaleOriginal(i)}
                                                          aria-expanded={expanded}
                                                          title={t("ShowOriginalAddress") || "Ver dirección original"}>
                                                          <i className={`bx ${expanded ? "bx-chevron-up" : "bx-chevron-down"}`} />
                                                        </button>
                                                      )}
                                                    </div>
                                                  </td>
                                                  <td style={{ minWidth: 160 }}>
                                                    <div className="d-flex align-items-center gap-1">
                                                      {s.originLocked ? (
                                                        /* Origen encadenado al destino de la escala anterior: no editable. */
                                                        <Input
                                                          bsSize="sm"
                                                          value={s.origin}
                                                          readOnly
                                                          disabled={s.deleted}
                                                          title={t("ChainedOriginHint") || "Origen tomado del destino de la escala anterior"}
                                                        />
                                                      ) : (
                                                        <PlaceAutocompleteInput
                                                          size="sm"
                                                          value={s.origin}
                                                          disabled={s.deleted}
                                                          onChange={(v) => changeScale(i, "origin", v)}
                                                          onSelect={(place) => selectScalePlace(i, "origin", place)}
                                                        />
                                                      )}
                                                      {s.origin_lat != null
                                                        ? <i className="bx bx-map-pin text-success" title={t("GeocodedOk") || "Ubicación resuelta"} />
                                                        : <i className="bx bx-map-pin text-muted" title={t("NotGeocoded") || "Sin ubicación"} />}
                                                    </div>
                                                  </td>
                                                  <td style={{ minWidth: 160 }}>
                                                    <div className="d-flex align-items-center gap-1">
                                                      <PlaceAutocompleteInput
                                                        size="sm"
                                                        value={s.destination}
                                                        disabled={s.deleted}
                                                        onChange={(v) => changeScale(i, "destination", v)}
                                                        onSelect={(place) => selectScalePlace(i, "destination", place)}
                                                      />
                                                      {s.destination_lat != null
                                                        ? <i className="bx bx-map-pin text-success" title={t("GeocodedOk") || "Ubicación resuelta"} />
                                                        : <i className="bx bx-map-pin text-muted" title={t("NotGeocoded") || "Sin ubicación"} />}
                                                    </div>
                                                  </td>
                                                  <td style={{ width: 100, maxWidth: 100 }}><Input bsSize="sm" className="w-100" value={s.passenger ?? ""} disabled={s.deleted}
                                                    onChange={(e) => changeScale(i, "passenger", e.target.value)} /></td>
                                                  <td style={{ width: 100, maxWidth: 100 }}><Input bsSize="sm" className="w-100" value={s.cost_center ?? ""} disabled={s.deleted}
                                                    onChange={(e) => changeScale(i, "cost_center", e.target.value)} /></td>
                                                  <td style={{ width: 150, maxWidth: 150 }}><Input bsSize="sm" className="w-100" type="datetime-local" value={s.start_time ?? ""} disabled={s.deleted}
                                                    onChange={(e) => changeScale(i, "start_time", e.target.value)} /></td>
                                                  <td style={{ width: 150, maxWidth: 150 }}><Input bsSize="sm" className="w-100" type="datetime-local" value={s.end_time ?? ""} disabled={s.deleted}
                                                    onChange={(e) => changeScale(i, "end_time", e.target.value)} /></td>
                                                  {/* PT-4078: kms de la escala editable al
                                                      alza o a la baja (sin tope al original). */}
                                                  <td style={{ width: 90, minWidth: 80 }}><Input bsSize="sm" className="w-100" type="number" step="1" min="0" value={s.kms} disabled={s.deleted}
                                                    invalid={!!editErrors[i]}
                                                    onChange={(e) => changeScaleDown(i, "kms", "_origKms", e.target.value, { round: true })} /></td>
                                                  {/* Duración en MINUTO REDONDEADO (step=1),
                                                      editable al alza o a la baja (PT-4078). */}
                                                  <td style={{ width: 110, minWidth: 90 }}>
                                                    <Input bsSize="sm" className="w-100" type="number" step="1" min="0" value={s.duration_min} disabled={s.deleted}
                                                      invalid={!!editErrors[i]}
                                                      onChange={(e) => changeScaleDown(i, "duration_min", "_origDurationMin", e.target.value, { round: true })} />
                                                    {editErrors[i] && (
                                                      <small className="text-danger d-block">{editErrors[i]}</small>
                                                    )}
                                                  </td>
                                                  <td style={{ width: 48 }} className="text-center">
                                                    <Button color={s.deleted ? "outline-secondary" : "outline-danger"} size="sm"
                                                      onClick={() => removeScale(i)} title={s.deleted ? t("Undo") : t("DeleteScale")}>
                                                      <i className={`bx ${s.deleted ? "bx-undo" : "bx-trash"}`} />
                                                    </Button>
                                                  </td>
                                                </tr>
                                                {/* A3: fila colapsable con la dirección ORIGINAL de
                                                    creación (snapshotada en _origOrigin/_origDestination).
                                                    Puramente visual: no toca payload ni backend. */}
                                                {!s.deleted && (
                                                  <tr className="svc-scale-original-row">
                                                    <td colSpan={10} className="p-0 border-0">
                                                      <Collapse isOpen={expanded}>
                                                        <div className="px-3 py-2 bg-light small">
                                                          <div className="text-muted fw-bold mb-1">
                                                            <i className="bx bx-map me-1" />
                                                            {t("OriginalAddressCreation") || "Dirección original (creación)"}
                                                          </div>
                                                          {isNewScale ? (
                                                            <div className="text-muted">{t("NewScaleNoOriginal") || "— (escala nueva)"}</div>
                                                          ) : (
                                                            <Row>
                                                              <Col md={6}>
                                                                <span className="text-muted">{t("Origin")}: </span>
                                                                {s._origOrigin || "—"}
                                                              </Col>
                                                              <Col md={6}>
                                                                <span className="text-muted">{t("Destination")}: </span>
                                                                {s._origDestination || "—"}
                                                              </Col>
                                                            </Row>
                                                          )}
                                                        </div>
                                                      </Collapse>
                                                    </td>
                                                  </tr>
                                                )}
                                                </React.Fragment>
                                                )
                                              }}
                                            </Draggable>
                                          )
                                        })
                                      })()
                                    )}
                                    {dropProvided.placeholder}
                                  </tbody>
                                )}
                              </Droppable>
                            </Table>
                          </DragDropContext>
                          <div className="d-flex gap-2 flex-wrap">
                            <Button color="light" size="sm" onClick={addScale}>
                              <i className="bx bx-plus me-1" /> {t("AddScale")}
                            </Button>
                            <div className="ms-auto d-flex gap-2">
                              <Button color="light" size="sm" onClick={cancelEdit} disabled={saving || rebuildingRoute}>{t("Cancel")}</Button>
                              <Button color="primary" size="sm" className="sv-search-btn" onClick={() => saveEdit()} disabled={saving || rebuildingRoute || hasEditErrors}>
                                {saving && !rebuildingRoute ? <Spinner size="sm" /> : <><i className="bx bx-save me-1" />{t("SaveChanges")}</>}
                              </Button>
                              {/* Si cambió el trazado (escalas agregadas/editadas),
                                  se ofrece guardar y planear la ruta en un paso. */}
                              {scalesDirty && (
                                <Button color="success" size="sm"
                                  onClick={() => saveEdit({ thenRebuild: true })}
                                  disabled={saving || rebuildingRoute || hasEditErrors}
                                  title={t("SaveAndPlanRouteHint") || "Guarda las escalas y reconstruye el recorrido"}>
                                  {rebuildingRoute
                                    ? <Spinner size="sm" />
                                    : <><i className="bx bx-git-repo-forked me-1" />{t("SaveAndPlanRoute") || "Guardar y planear ruta"}</>}
                                </Button>
                              )}
                            </div>
                          </div>
                          <small className="text-muted d-block mt-2">
                            <i className="bx bx-info-circle me-1" />{t("ScaleRecalcHint")}
                          </small>
                        </>
                      )}
                    </TabPane>

                    {/* Evidencias (PODs) */}
                    <TabPane tabId="pods">
                      <ServiceValidationPods code={code} pods={pods}
                        canWrite={canWrite && !validated} heldByMe={heldByMe}
                        authHeader={authHeader} onChanged={load} embedded />
                    </TabPane>

                    {/* Pasajeros */}
                    <TabPane tabId="riders">
                      {riders.length === 0 ? (
                        <p className="text-muted mb-0">{t("NoRidersRegistered")}</p>
                      ) : (
                        <Table size="sm" className="mb-0">
                          <thead><tr><th>#</th><th>{t("Name")}</th><th>{t("Phone") || "Teléfono"}</th></tr></thead>
                          <tbody>
                            {riders.map((r, i) => (
                              <tr key={r.id || i}>
                                <td>{i + 1}</td><td>{dash(r.name)}</td><td>{dash(r.phone)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </TabPane>

                    {/* Timeline */}
                    <TabPane tabId="timeline">
                      {/* Timeline con el mismo diseño, animación e i18n que el
                          detalle de servicio, pero con la paleta del validador
                          (clase sv-timeline). Se alimenta del service de
                          getServiceAdmin (tripService) + los eventos aplanados
                          de getTripsTimeline (timeline). */}
                      <ServiceValidationTimeline
                        service={tripService || {}}
                        timelineData={timeline}
                      />
                        </TabPane>
                      </TabContent>
                </CardBody>
              </Card>

              {/* Bitácora: bajo los tabs (columna izquierda) */}
              {auditCard}
            </Col>

            {/* DERECHA: panel lateral. Aloja "Montos del servicio" (interactivo:
                Calcular / Editar / Validar), Datos de pago, Mapa/Recorrido y
                Actividad. */}
            <Col lg={4}>
              {/* Montos del servicio: panel interactivo. Aloja el flujo de
                  montos antes repartido en el tab "Montos" (eliminado): Calcular
                  montos (recalcula vía backend), edición manual del monto
                  proveedor (toggle Editar + confirmación, edición a la baja y
                  bitácora intactas) y el CTA Validar. Lee de la fuente de verdad
                  (`detail`) y reutiliza EXACTAMENTE los mismos handlers/estados
                  (sin cambiar endpoints ni payloads). Se coloca ENCIMA de "Datos
                  de pago". */}
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="text-muted text-uppercase small mb-0">
                      {t("ServiceAmounts") || "Montos del servicio"}
                    </h6>
                    {/* Calcular montos (PT-4066): NO calcula en UI. Llama al
                        backend (PATCH de edición) que recalcula a tres niveles y
                        luego recarga el detalle con los montos recalculados. */}
                    {!validated && (
                      <Button color="outline-primary" size="sm" className="sv-btn-icon-text"
                        data-tour="svd-calc-amounts"
                        onClick={handleCalculateAmounts} disabled={recalcAmounts || acting || !heldByMe}
                        title={!heldByMe ? t("TakeHoldFirst") : t("CalculateAmounts")}>
                        {recalcAmounts
                          ? <Spinner size="sm" />
                          : <><i className="bx bx-calculator me-1" />{t("CalculateAmounts")}</>}
                      </Button>
                    )}
                  </div>
                  {/* Guarda on-hold (PT-4074): no se pueden calcular montos si el
                      servicio no está tomado por el usuario actual. */}
                  {!validated && !heldByMe && (
                    <Alert color="warning" className="py-2 mb-2">
                      <i className="bx bx-lock-alt me-1" /> {t("TakeHoldFirst")}
                    </Alert>
                  )}
                  {/* Montos del backend. El ÚNICO monto editable es el monto
                      proveedor (supplier_amount): el backend lo acepta como
                      `requested_supplier_amount` al validar. PT-4078: el override
                      se respeta al alza o a la baja. Comisión, total y monto
                      cliente son derivados/sin override: solo lectura. La edición
                      manual del monto proveedor (PT-4070) es OPCIONAL: solo tras
                      "Calcular montos" y activando el toggle "Editar". */}
                  <Table borderless size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td>{t("SupplierAmount")}</td>
                        <td className="text-end">
                          {amountsCalculated && amountEditOpen && !validated ? (
                            <div className="d-inline-block" style={{ maxWidth: 140 }}>
                              <Input type="number" bsSize="sm" className="text-end"
                                min={0} step="0.01"
                                value={amountEdits?.supplier_amount ?? (detail?.supplier_amount ?? 0)}
                                invalid={!!amountErrors.supplier_amount}
                                onChange={(e) => changeAmountDown("supplier_amount", detail?.supplier_amount ?? 0, e.target.value)} />
                            </div>
                          ) : (
                            fmtMXN(amountEdits?.supplier_amount ?? (detail?.supplier_amount ?? 0))
                          )}
                          {/* Toggle "Editar": habilita la edición manual del monto
                              proveedor. Solo visible tras calcular y si no está
                              validado. No borra amountEdits al cerrar. Al abrir
                              reinicia la confirmación (PT-4075). */}
                          {amountsCalculated && !validated && (
                            <Button size="sm" color="light" className="ms-2 sv-btn-icon-text"
                              data-tour="svd-edit-amount"
                              onClick={() => setAmountEditOpen((v) => {
                                const next = !v
                                if (next) setAmountEditConfirmed(false)
                                return next
                              })}>
                              <i className="bx bx-edit-alt me-1" />{t("EditAmount") || "Editar"}
                            </Button>
                          )}
                          {/* Confirmación del cambio manual (PT-4075): mientras
                              haya un monto editado distinto al del backend, el
                              monto no se aplica/envía ni se habilita Validar hasta
                              confirmar con este botón explícito. */}
                          {amountEditOpen && !validated && amountDiffers && (
                            <div className="mt-2 d-flex align-items-center justify-content-end gap-2">
                              <span className="text-muted small">{t("ConfirmManualAmountChange")}</span>
                              <Button size="sm" color={amountEditConfirmed ? "success" : "primary"}
                                className="sv-btn-icon-text"
                                disabled={amountEditConfirmed}
                                onClick={() => setAmountEditConfirmed(true)}>
                                <i className="bx bx-check me-1" />
                                {amountEditConfirmed ? (t("Confirmed") || "Confirmado") : t("Confirm")}
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>{t("Commission")}</td>
                        <td className="text-end">{fmtMXN(detail?.commission ?? 0)}</td>
                      </tr>
                      <tr>
                        <td>{t("SupplierAmountTotal")}</td>
                        <td className="text-end fw-bold">{fmtMXN(detail?.supplier_amount_total ?? 0)}</td>
                      </tr>
                      <tr>
                        <td>{t("CustomerAmount")}</td>
                        <td className="text-end">{fmtMXN(detail?.customer_amount ?? 0)}</td>
                      </tr>
                    </tbody>
                  </Table>
                  {hasAmountErrors && (
                    <small className="text-danger d-block mt-1">{t("EditOnlyDownError")}</small>
                  )}
                  {/* Motivo que bloquea la validación, como feedback informativo. */}
                  {validateBlockedReason && !validated && (
                    <Alert color="warning" className="py-2 mt-2 mb-0">
                      <i className="bx bx-lock-alt me-1" /> {validateBlockedReason}
                    </Alert>
                  )}
                  {/* CTA Validar (reubicado desde la barra de acciones). Mismas
                      guardas/confirmaciones: abre el overlay validateConfirmOpen
                      → handleValidate, sin cambios de lógica ni endpoints. */}
                  {canValidate && !validated && (
                    <div className="d-grid mt-3">
                      <Button color="primary" className="sv-search-btn sv-btn-icon-text"
                        data-tour="svd-validate-cta"
                        onClick={() => setValidateConfirmOpen(true)}
                        disabled={!canDoValidate || acting || amountEditPending}
                        title={isCancelled
                          ? (t("ServiceCancelledNoActions") || "El servicio está cancelado")
                          : (amountEditPending
                            ? t("ConfirmManualAmountChange")
                            : (validateBlockedReason || t("Validate")))}>
                        {acting ? <Spinner size="sm" /> : <><i className="bx bx-check-shield me-1" />{t("Validate")}</>}
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Datos de pago: se poblan cuando el servicio se agrega a un
                  lote en el ERP (evento NATS). Se muestra siempre; cuando no
                  hay datos aún, cada campo cae a "—". Va bajo la tarjeta de
                  "Montos del servicio". */}
              <Card>
                <CardBody>
                  <h6 className="text-muted text-uppercase small mb-2">
                    {t("PaymentData") || "Datos de pago"}
                  </h6>
                  <Table borderless size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td>{t("Lot") || "Lote"}</td>
                        <td className="text-end">{dash(detail?.lot_name)}</td>
                      </tr>
                      <tr>
                        <td>{t("PaymentDate") || "Fecha de pago"}</td>
                        <td className="text-end">
                          {(() => {
                            const pd = detail?.payment_date
                            if (!pd) return dash(pd)
                            const d = new Date(pd)
                            return Number.isNaN(d.getTime())
                              ? pd
                              : d.toLocaleDateString("es-MX")
                          })()}
                        </td>
                      </tr>
                      <tr>
                        <td>{t("ClabeAccount") || "Cuenta CLABE"}</td>
                        <td className="text-end">{dash(detail?.clabe)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </CardBody>
              </Card>

              {/* Mapa/Recorrido */}
              {mapCard}

              {/* Actividad del servicio (ediciones/reconstrucciones) bajo el mapa */}
              {activityCard}
            </Col>
          </Row>
        )}

        {/* Overlay "Cierre sin cliente" (PT-4066): tipo de cierre + aceptar/cancelar.
            Al aceptar valida con close_without_customer=true. */}
        <Modal isOpen={closeNoCustomerOpen} toggle={() => setCloseNoCustomerOpen(!closeNoCustomerOpen)}>
          <ModalHeader toggle={() => setCloseNoCustomerOpen(!closeNoCustomerOpen)}>
            {t("CloseWithoutCustomer")}
          </ModalHeader>
          <ModalBody>
            <FormGroup className="mb-0">
              <Label className="mb-1">{t("CloseType")}</Label>
              <Select
                classNamePrefix="select2-selection"
                className="sv-filter-select"
                placeholder={t("SelectCloseType")}
                isSearchable={false}
                options={CLOSE_OPTIONS_NO_NA}
                value={CLOSE_OPTIONS_NO_NA.find((o) => o.value === cwcClose) || null}
                onChange={(opt) => setCwcClose(opt?.value ?? "")}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setCloseNoCustomerOpen(false)} disabled={acting}>
              {t("Cancel")}
            </Button>
            <Button color="primary" onClick={handleCloseWithoutCustomer} disabled={!cwcClose || acting}>
              {acting ? <Spinner size="sm" /> : t("Accept")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Overlay de confirmación previo a Validar (PT-4066). */}
        <Modal isOpen={validateConfirmOpen} toggle={() => setValidateConfirmOpen(!validateConfirmOpen)}>
          <ModalHeader toggle={() => setValidateConfirmOpen(!validateConfirmOpen)}>
            {t("ConfirmValidation")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-0">{t("ConfirmValidationBody")}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setValidateConfirmOpen(false)} disabled={acting}>
              {t("Cancel")}
            </Button>
            <Button color="primary" disabled={acting}
              onClick={async () => { setValidateConfirmOpen(false); await handleValidate() }}>
              {acting ? <Spinner size="sm" /> : t("Accept")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Confirmación de cancelación de servicio (acción destructiva). */}
        <Modal isOpen={cancelOpen} toggle={() => setCancelOpen(!cancelOpen)}>
          <ModalHeader toggle={() => setCancelOpen(!cancelOpen)}>
            {t("CancelService") || "Cancelar servicio"}
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">
              {t("CancelServiceConfirm")
                || "¿Seguro que deseas cancelar este servicio? El servicio quedará cancelado y se notificará al ERP."}
            </p>
            <FormGroup className="mb-0">
              <Label className="mb-1">
                {t("Reason") || "Motivo"} <span className="text-danger">*</span>
              </Label>
              <Input type="textarea" rows={3} value={cancelReason}
                invalid={!cancelReason.trim()}
                placeholder={t("CancelReasonRequired") || "El motivo es obligatorio"}
                onChange={(e) => setCancelReason(e.target.value)} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setCancelOpen(false)} disabled={acting}>
              {t("Back") || t("Cancel")}
            </Button>
            <Button color="danger" onClick={handleCancel} disabled={acting || !cancelReason.trim()}>
              {acting ? <Spinner size="sm" /> : (t("CancelService") || "Cancelar servicio")}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={supportOpen} toggle={() => setSupportOpen(!supportOpen)}>
          <ModalHeader toggle={() => setSupportOpen(!supportOpen)}>{t("RequestSupport")}</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>{t("Subject")}</Label>
              <Input value={supportSubject} onChange={(e) => setSupportSubject(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>{t("Description")}</Label>
              <Input type="textarea" value={supportDescription} onChange={(e) => setSupportDescription(e.target.value)} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setSupportOpen(false)}>{t("Cancel")}</Button>
            <Button color="primary" onClick={handleSupport} disabled={!supportSubject}>{t("Send")}</Button>
          </ModalFooter>
        </Modal>

        {/* Overlay: mapa a pantalla casi completa. Monta su propia instancia
            de ServiceRouteMap (se desmonta al cerrar). */}
        <Modal isOpen={mapFullscreen} toggle={() => setMapFullscreen(false)}
          size="xl" fullscreen="lg" contentClassName="h-100">
          <ModalHeader toggle={() => setMapFullscreen(false)}>
            <i className="bx bx-map-alt me-1" />{t("Route") || "Recorrido"} · {code}
          </ModalHeader>
          <ModalBody className="sv-map-fullscreen-modalbody" style={{ padding: 0 }}>
            {mapFullscreen && hasMapLayers && (
              <div className="sv-map-fullscreen-body">
                {/* Panel de escalas del servicio. Clic enfoca el mapa (marker
                    animado) y resalta el punto activo. En escritorio va a la
                    izquierda (260px); en móvil se apila arriba con alto acotado. */}
                {mapPois.length > 0 && (
                  <div className="sv-map-fullscreen-scales">
                    <div className="px-3 py-2 border-bottom fw-bold small text-muted text-uppercase">
                      <i className="bx bx-map-pin me-1" />{t("Scales") || "Escalas"}
                    </div>
                    <ul className="list-group list-group-flush">
                      {mapPois.map((p, i) => {
                        const active = focusPoint
                          && Math.abs(focusPoint.lat - p.lat) < 1e-6
                          && Math.abs(focusPoint.lng - p.lng) < 1e-6
                        const dot = p.kind === "start" ? "#34C38F"
                          : p.kind === "end" ? "#DB2323" : "#539EF3"
                        return (
                          <li key={`${p.kind}-${i}`}
                            className={`list-group-item list-group-item-action py-2 ${active ? "active" : ""}`}
                            style={{ cursor: "pointer", background: active ? undefined : "transparent" }}
                            onClick={() => focusOnMap(p.lat, p.lng, p.text)}
                            title={t("ShowOnMap") || "Ver en el mapa"}>
                            <div className="d-flex align-items-center gap-2">
                              <span style={{
                                width: 10, height: 10, borderRadius: "50%",
                                background: dot, flexShrink: 0,
                                boxShadow: active ? "0 0 0 3px rgba(255,255,255,.6)" : "none",
                              }} />
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className={`small fw-medium text-truncate ${active ? "" : "text-dark"}`}>{p.label}</div>
                                <div className={`small text-truncate ${active ? "" : "text-muted"}`}>{p.text}</div>
                              </div>
                              <CopyCoordsButton lat={p.lat} lng={p.lng}
                                title={t("CopyCoords") || "Copiar lat,lng"}
                                className={active ? "text-white" : ""} />
                              <i className="bx bx-target-lock" />
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

                <div className="sv-map-fullscreen-map">
                  <ServiceRouteMap
                    layers={mapLayers}
                    focusPoint={focusPoint}
                    deviationOverlay={deviationOverlay}
                    showDeviationAddresses={showDeviationAddresses}
                    features={{ route: true, speed: false, distanceMarkers: false, basemaps: true, elevation: false, stats: true, export: true, replay: true }}
                    height="100%"
                    serviceCode={code} />
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>

        {/* Tutorial interactivo de la vista de validación (manual). */}
        <ServiceValidationTour t={t} variant="detail"
          open={tourOpen} onClose={() => setTourOpen(false)} />
      </Container>
    </div>
  )
}

export default withTranslation()(ServiceValidationDetail)
